import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import { UiPanelComponent, UiTabGroupComponent } from '@template-ui/components';
import {
  WorkflowDetailComposerComponent,
  type WorkflowNoticeRequirementRequest,
} from './workflow-detail-composer.component';
import { reviewWorkDeclaredStatuses } from '@app/workflow-dashboards/workflow-review-work';
import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  ViewChild,
  TemplateRef,
  Output,
  inject,
  ElementRef,
} from '@angular/core';
import {
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { FormsModule, NgModel } from '@angular/forms';
import { CdkMenu, CdkMenuItem, CdkMenuTrigger } from '@angular/cdk/menu';
import {
  WorkflowDefinitionDocument,
  WorkflowDefinitionNode,
  WorkflowStepDeclaration,
  WorkflowCapabilityReference,
  requireWorkflowPayloadVersion,
} from './platform-workflow-definition-lifecycle.models';
import {
  replacePresentation,
  hasEditableRouting,
  enableEditableRouting,
} from './workflow-presentation-authoring';
import { WorkflowPresentationPreviewComponent } from './workflow-presentation-preview.component';
import { WorkflowStepComponentsEditorComponent } from './workflow-step-components-editor.component';
import {
  StepAuthoringCatalog,
  StepAuthoringView,
  StepCommand,
} from './workflow-step-composition';
import {
  capabilityIdentity,
  sameCapabilityReference,
  WorkflowCapabilityCatalogEntry,
} from './workflow-capability-authoring';
import {
  createDashboardPresentation,
  dashboardColumns,
  dashboardBusinessStatusChoices,
  dashboardFilterChoices,
  dashboardTileStatusChoices,
  dashboardStepProgressChoices,
  dashboardAssignmentChoices,
  hasDashboardAssignment,
  resolveDashboardPresentation,
  updateDashboardPresentation,
} from '@app/workflow-dashboards/workflow-dashboard-presentation';
import {
  WorkflowOwnerViewState,
  WorkflowOwnerFocus,
  captureWorkflowOwnerFocus,
  restoreWorkflowOwnerFocus,
} from './workflow-owner-view-state';
import type {
  WorkflowEditorBufferState,
  WorkflowEditorPendingScope,
} from './workflow-editor-pending-state';
import type { WorkflowStatusOwnerRequest } from './workflow-status-owner-request';
import { WorkflowLibrarySelectorComponent } from './workflow-library-selector.component';
import type { WorkflowLibraryEntry } from './workflow-library-selector.types';
import {
  applyDashboardElement,
  readDashboardElementChoices,
  type WorkflowDashboardElementChoice,
  type WorkflowDashboardElementTarget,
} from './workflow-dashboard-element-authoring';

type DashboardPresentation = ReturnType<typeof createDashboardPresentation>;
type DashboardSelection = { kind: 'tile' | 'filter' | 'column'; id: string };
type DashboardFilter = DashboardPresentation['filters'][number];
type DashboardTile = DashboardPresentation['tiles'][number];
type DashboardView = NonNullable<WorkflowStepDeclaration['dashboards']>[number];
export interface WorkflowDashboardNameDraft {
  workflowCode: string;
  definitionVersionCode: string;
  packReference: string;
  nodeId: string;
  nodeType: string;
  dashboardId: string;
  view: DashboardView['view'];
  target: DashboardSelection | null;
  originalLabel: string;
  value: string;
  error: string;
  owner?: string;
  context?: string;
}
type DashboardRenameDraft = WorkflowDashboardNameDraft;
export interface WorkflowDashboardSettingsOrigin {
  owner: string;
  context: string;
  source: string;
  view: string | null;
  nodeId: string;
  dashboardId: string;
  task: 'canvas' | 'settings' | 'manage';
  mode: 'edit' | 'try';
  generation: number;
}
export interface WorkflowDashboardNameOwnedDocumentChange {
  document: WorkflowDefinitionDocument;
  nodeId: string;
  dashboardId: string;
  reference: WorkflowCapabilityReference;
  owner: string;
  source: string;
  view: string | null;
  context: string;
  draft: WorkflowDashboardNameDraft;
}
export interface WorkflowDashboardRemovalRequest {
  nodeId: string;
  dashboardId: string;
  selection: string;
  owner: string;
  source: string;
  view: string;
  context: string;
}
export interface WorkflowDashboardSourceOwnedDocumentChange {
  document: WorkflowDefinitionDocument;
  nodeId: string;
  reference: WorkflowCapabilityReference;
  owner: string;
  source: string;
  view: string;
  context: string;
}
export interface WorkflowDashboardElementOwnedDocumentChange {
  document: WorkflowDefinitionDocument;
  nodeId: string;
  dashboardId: string;
  reference: WorkflowCapabilityReference;
  choice: WorkflowDashboardElementChoice;
  selection: DashboardSelection;
  owner: string;
  source: string;
  view: string;
  context: string;
}

@Component({
  selector: 'phms-workflow-presentation-editor',
  standalone: true,
  imports: [
    UiButtonDirective,
    UiControlDirective,
    UiPanelComponent,
    UiTabGroupComponent,
    CommonModule,
    FormsModule,
    MatDialogModule,
    CdkMenu,
    CdkMenuItem,
    CdkMenuTrigger,
    WorkflowPresentationPreviewComponent,
    WorkflowDetailComposerComponent,
    WorkflowStepComponentsEditorComponent,
    WorkflowLibrarySelectorComponent,
  ],
  templateUrl: './workflow-presentation-editor.component.html',
  styleUrl: './workflow-presentation-editor.component.scss',
})
export class WorkflowPresentationEditorComponent
  implements OnChanges, OnDestroy
{
  @Input() document: WorkflowDefinitionDocument | null = null;
  @Input() node: WorkflowDefinitionNode | null = null;
  @Input() scope: 'dashboard' | 'detail' | 'behavior' = 'dashboard';
  @Input() disabled = false;
  @Input() active = true;
  @Input() workEditorAvailable = false;
  @Input() immediateRoutingChanges = false;
  @Input() externalWorkflowStatuses = false;
  @Input() workflowStatusesView: (() => string | null) | null = null;
  @Input() latestDocument: (() => WorkflowDefinitionDocument | null) | null = null;
  @Input() ownerContext: (() => unknown) | null = null;
  @Input() formViewContext: (() => unknown) | null = null;
  @Input() guardPageSettings = false;
  @Input() guardContentLibrary = false;
  @Input() guardNoticeSettings = false;
  @Input() pageSettingsView: (() => string | null) | null = null;
  @Input() pageSettingsViewState: string | null | undefined = undefined;
  @Input() managedDashboardRemoval = false;
  @Input() dashboardRemovalView: (() => string | null) | null = null;
  @Input() dashboardRemovalViewState: string | null | undefined = undefined;
  @Input() managedDashboardSources = false;
  @Input() managedDashboardElements = false;
  @Input() managedDashboardSettings = false;
  @Input() dashboardSettingsView: (() => string | null) | null = null;
  @Input() dashboardSettingsViewState: string | null | undefined = undefined;
  @Input() dashboardNameSettlement: (() => boolean) | null = null;
  @Input() dashboardElementView: (() => string | null) | null = null;
  @Input() dashboardElementViewState: string | null | undefined = undefined;
  @Input() dashboardSourceView: (() => string | null) | null = null;
  @Input() dashboardSourceViewState: string | null | undefined = undefined;
  @Input() resetVersion = 0;
  @Input() responsibilityEditor: TemplateRef<unknown> | null = null;
  @Input() responsibilitySummary = '';
  @Input() beforeSelectionChange: (() => boolean) | null = null;
  @Input() focusResponsibility: {
    request: number;
    dashboardId?: string;
  } | null = null;
  @Input() focusForm: {
    capability: StepCommand['capability'];
    request: number;
  } | null = null;
  @Input() focusAction: {
    capability: StepCommand['capability'];
    request: number;
    outcomes?: boolean;
    outcomeCode?: string;
  } | null = null;
  @Input() roleCodes: string[] = [];
  @Input() roleCatalogAvailable = false;
  @Input() queueChangesPending = false;
  @Input() canUndoDashboard = false;
  @Output() readonly undoDashboard = new EventEmitter<void>();
  @Output() readonly dashboardRemovalRequested = new EventEmitter<WorkflowDashboardRemovalRequest>();
  @Output() readonly dashboardSourceOwnedDocumentChange = new EventEmitter<WorkflowDashboardSourceOwnedDocumentChange>();
  @Output() readonly dashboardElementOwnedDocumentChange = new EventEmitter<WorkflowDashboardElementOwnedDocumentChange>();
  @Output() readonly dashboardNameOwnedDocumentChange = new EventEmitter<WorkflowDashboardNameOwnedDocumentChange>();
  @Output() readonly openActions = new EventEmitter<StepCommand | undefined>();
  @Output() readonly openOutcomes = new EventEmitter<StepCommand>();
  @Output() readonly openForm = new EventEmitter<StepCommand>();
  @Output() readonly openAdvanced = new EventEmitter<void>();
  @Input() focusBlock: { id: string } | null = null;
  @Output() readonly documentChange =
    new EventEmitter<WorkflowDefinitionDocument>();
  @Output() readonly pendingChangesChange = new EventEmitter<boolean>();
  @Output() readonly openStep = new EventEmitter<string>();
  @Output() readonly openBehavior = new EventEmitter<void>();
  @Output() readonly openPaths = new EventEmitter<void>();
  @Output() readonly openDirectPath = new EventEmitter<void>();
  @Output() readonly openOutcomePath = new EventEmitter<{ command: StepCommand; outcomeCode: string }>();
  @Output() readonly openRequirements = new EventEmitter<void>();
  @Output() readonly noticeRequirementRequested =
    new EventEmitter<WorkflowNoticeRequirementRequest>();
  @Output() readonly openWorkflowStatuses = new EventEmitter<string>();
  @Output() readonly workflowStatusesOwnerRequested = new EventEmitter<WorkflowStatusOwnerRequest>();
  @Output() readonly assignmentRequested = new EventEmitter<void>();
  componentCatalog: StepAuthoringCatalog | null = null;
  componentCapabilities: WorkflowCapabilityCatalogEntry[] = [];
  queueSettingsOpen = true;
  inspectorOpen = true;
  canvasSize: 'available' | 'phone' = 'available';
  canvasWidth = 0;
  addToolsOpen = false;
  responsibilitySettingsOpen = false;
  mobilePanel: 'page' | 'settings' = 'page';
  @ViewChild('dashboardInspector')
  private dashboardInspector?: ElementRef<HTMLElement>;
  @ViewChild('dashboardPanels', { read: ElementRef })
  private dashboardPanels?: ElementRef<HTMLElement>;
  @ViewChild('dashboardInspectorToggle')
  private dashboardInspectorToggle?: ElementRef<HTMLButtonElement>;
  @ViewChild('dashboardSettingsButton')
  private dashboardSettingsButton?: ElementRef<HTMLButtonElement>;
  @ViewChild('addElementButton')
  private addElementButton?: ElementRef<HTMLButtonElement>;
  private lastResponsibilityFocus: typeof this.focusResponsibility = null;
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly ownerViews = inject(WorkflowOwnerViewState, { optional: true }) ?? new WorkflowOwnerViewState();
  private ownerViewKey = '';
  private ownerFocus: WorkflowOwnerFocus | null = null;
  private ownerFocusSelection = '';
  private ownerFocusRequest = 0;
  private ownerDocument: WorkflowDefinitionDocument | null = null;
  private ownerNodeId = '';
  private ownerDashboardId = '';
  private ownerActive = true;
  private ownerRestorePending = false;

  @HostListener('focusin', ['$event'])
  rememberOwnerFocus(event: FocusEvent): void {
    this.ownerFocusRequest++;
    if (this.scope !== 'dashboard') return;
    this.ownerFocus = captureWorkflowOwnerFocus(this.element.nativeElement, event.target);
    this.ownerFocusSelection = JSON.stringify([this.dashboard?.id, this.selection]);
  }

  private rememberOwnerView(): void {
    if (!this.ownerViewKey || this.ownerRestorePending) return;
    const dashboardId = this.selectedDashboard || this.ownerDashboardId;
    const dashboard = this.ownerDocument?.nodes.find(node => node.id === this.ownerNodeId)?.declaration?.dashboards?.find(item => item.id === dashboardId);
    this.ownerViews.remember(this.ownerViewKey, {
      dashboardId,
      view: dashboard?.view,
      selection: this.selection,
      queueSettingsOpen: this.queueSettingsOpen,
      inspectorOpen: this.inspectorOpen,
      responsibilitySettingsOpen: this.responsibilitySettingsOpen,
      mobilePanel: this.mobilePanel,
      canvasSize: this.canvasSize,
      dashboardTask: this.dashboardTask,
      settingsReturn: this.settingsReturn,
      focus: this.ownerFocus,
      focusSelection: this.ownerFocusSelection,
    });
  }

  private restoreOwnerView(): boolean {
    this.ownerRestorePending = false;
    const retained = this.ownerViews.read<{
      dashboardId: string;
      view?: DashboardView['view'];
      selection: DashboardSelection | null;
      queueSettingsOpen: boolean;
      inspectorOpen: boolean;
      responsibilitySettingsOpen: boolean;
      mobilePanel: 'page' | 'settings';
      canvasSize: 'available' | 'phone';
      dashboardTask?: 'canvas' | 'settings' | 'manage';
      settingsReturn?: { dashboardId: string; selection: DashboardSelection | null } | null;
      focus: WorkflowOwnerFocus | null;
      focusSelection: string;
    }>(this.ownerViewKey);
    if (!retained || !this.document || !this.node) return false;
    const dashboards = this.declaration?.dashboards?.filter(item => item.id === retained.dashboardId) ?? [];
    if (this.document.nodes.filter(node => node.id === this.node!.id).length !== 1 ||
      dashboards.length !== 1 || !retained.view || !sameCapabilityReference(dashboards[0].view, retained.view)) {
      this.error = 'The previously selected dashboard is unavailable or ambiguous. Select a dashboard to inspect its settings.';
      return false;
    }
    this.selectedDashboard = retained.dashboardId;
    if (retained.selection && !this.componentCatalog) {
      this.ownerRestorePending = true;
      return false;
    }
    this.selection = retained.selection && this.resolveDashboardRenameTarget(this.document, this.node.id, retained.dashboardId, retained.selection)
      ? retained.selection : null;
    if (retained.selection && !this.selection)
      this.error = 'The previously selected dashboard element is unavailable or ambiguous. Dashboard settings remain available.';
    this.queueSettingsOpen = this.selection ? retained.queueSettingsOpen : true;
    this.inspectorOpen = retained.inspectorOpen;
    this.responsibilitySettingsOpen = retained.responsibilitySettingsOpen;
    this.mobilePanel = retained.mobilePanel;
    this.canvasSize = retained.canvasSize;
    this.dashboardTask = retained.dashboardTask ?? 'canvas';
    this.settingsReturn = retained.settingsReturn ?? null;
    this.ownerFocus = retained.focus;
    this.ownerFocusSelection = retained.focusSelection;
    return true;
  }

  onComponentCatalog(catalog: StepAuthoringCatalog): void {
    this.componentCatalog = catalog;
    if (this.ownerRestorePending && !this.hasPendingChanges() && this.restoreOwnerView() && this.active)
      this.restoreOwnerFocus();
  }

  private restoreOwnerFocus(): void {
    const request = ++this.ownerFocusRequest;
    const key = this.ownerViewKey;
    const selection = JSON.stringify([this.dashboard?.id, this.selection]);
    setTimeout(() => {
      if (this.ownerViews.focusSuspended() || request !== this.ownerFocusRequest || !this.active || key !== this.ownerViewKey || selection !== JSON.stringify([this.dashboard?.id, this.selection])) return;
      if (selection === this.ownerFocusSelection && restoreWorkflowOwnerFocus(this.element.nativeElement, this.ownerFocus)) return;
      if (!this.element.nativeElement.closest('[hidden], [inert]'))
        this.dashboardInspector?.nativeElement.querySelector<HTMLElement>('[data-dashboard-heading]')?.focus({ preventScroll: true });
    });
  }
  private dashboardRename: DashboardRenameDraft | null = null;
  dashboardTask: 'canvas' | 'settings' | 'manage' = 'canvas';
  dashboardPreviewResetVersion = 0;
  private settingsAuthority = '';
  private settingsRenderedView: string | null = null;
  private settingsGeneration = 0;
  private settingsFocusRequest = 0;
  private settingsDestroyed = false;
  private settingsReturn: {
    dashboardId: string;
    selection: DashboardSelection | null;
  } | null = null;
  private nameEmission: WorkflowDashboardNameOwnedDocumentChange | null = null;
  private restoringDashboardModel = false;
  get dashboardRenameSnapshot(): WorkflowDashboardNameDraft | null {
    return this.dashboardRename ? structuredClone(this.dashboardRename) : null;
  }
  private settingsId(): string {
    const rows = this.declaration?.dashboards;
    return (
      this.selectedDashboard ||
      (Array.isArray(rows) && typeof rows[0]?.id === 'string' ? rows[0].id : '')
    );
  }
  private settingsTarget(id: string, document = this.sourceDocument()) {
    if (!id) return null;
    const nodes = Array.isArray(document?.nodes)
      ? document.nodes.filter((node) => node?.id === this.node?.id)
      : [];
    const rows =
      nodes.length === 1 &&
      nodes[0].typeCode === 'human_step' &&
      Array.isArray(nodes[0].declaration?.dashboards)
        ? nodes[0].declaration!.dashboards!.filter((row) => row?.id === id)
        : [];
    return document && rows.length === 1
      ? { document, node: nodes[0], dashboard: rows[0] }
      : null;
  }
  private settingsSource(dashboard: DashboardView): {
    label: string;
    reason: string;
  } {
    try {
      const rows = Array.isArray(this.componentCatalog?.views)
        ? this.componentCatalog.views.filter(
            (view) =>
              view && sameCapabilityReference(view.capability, dashboard?.view),
          )
        : [];
      const supported = rows.length === 1 && this.canComposeView(rows[0]);
      return {
        label: rows.length === 1 ? rows[0].label : 'Saved source unavailable',
        reason: supported
          ? ''
          : 'This exact saved source is unavailable or ambiguous. Its original settings are retained.',
      };
    } catch {
      return {
        label: 'Saved source unavailable',
        reason:
          'The exact saved source has an unsupported shape. Its original settings are retained.',
      };
    }
  }
  private settingsStartingView(dashboard: DashboardView): string {
    const filters = dashboard?.presentation?.filters;
    if (!Array.isArray(filters)) return 'Saved starting view retained';
    return (
      filters
        .flatMap((filter) => {
          if (
            !filter ||
            typeof filter.label !== 'string' ||
            typeof filter.initialValue !== 'string'
          )
            return ['Saved filter unavailable'];
          const value = filter.initialValue;
          if (filter.field === 'history')
            return [
              this.historyChoices.find((choice) => choice.code === value)
                ?.label ?? `${filter.label}: ${value} (saved)`,
            ];
          if (filter.field === 'assignment')
            return [
              value
                ? (this.assignmentChoices.find(
                    (choice) => choice.code === value,
                  )?.label ?? `${value} (saved)`)
                : 'All assignments',
            ];
          return value ? [`${filter.label}: ${value} (saved)`] : [];
        })
        .join(' · ') || 'No starting filter values'
    );
  }
  get dashboardSettingsState() {
    const document = this.sourceDocument(),
      target = this.settingsTarget(this.settingsId(), document);
    const origin: WorkflowDashboardSettingsOrigin = {
      owner: this.removalOwnerKey(),
      context: this.removalContext(),
      source: JSON.stringify(document),
      view: this.dashboardSettingsView?.() ?? null,
      nodeId: this.node?.id ?? '',
      dashboardId: this.settingsId(),
      task: this.dashboardTask,
      mode: this.dashboardMode,
      generation: this.settingsGeneration,
    };
    const raw =
      target?.node.declaration?.dashboards ?? this.declaration?.dashboards;
    const rows = (Array.isArray(raw) ? raw : []).map((dashboard, index) => {
      const unique =
        typeof dashboard?.id === 'string' &&
        raw!.filter((row) => row?.id === dashboard.id).length === 1;
      const source = this.settingsSource(dashboard);
      const startingView = this.settingsStartingView(dashboard);
      const roles = dashboard?.audienceRoleCodes;
      return {
        id: unique ? dashboard.id : '',
        label:
          typeof dashboard?.label === 'string'
            ? dashboard.label
            : 'Unavailable dashboard',
        sourceLabel: source.label,
        startingView,
        audienceLabel:
          roles == null
            ? 'Everyone with record access'
            : Array.isArray(roles) &&
                roles.every((role) => typeof role === 'string')
              ? roles.map((role) => this.roleLabel(role)).join(', ') ||
                'Choose roles'
              : 'Saved audience unavailable',
        current: unique && dashboard.id === origin.dashboardId,
        unavailableReason: unique
          ? source.reason
          : 'Saved dashboard identity is missing or ambiguous.',
        technical: { index, dashboard },
      };
    });
    const source = target
      ? this.settingsSource(target.dashboard)
      : {
          label: 'Saved source unavailable',
          reason:
            'Select one uniquely identified dashboard to inspect its settings.',
        };
    const presentation = target?.dashboard.presentation;
    const opaque =
      presentation != null &&
      (typeof presentation !== 'object' ||
        Array.isArray(presentation) ||
        presentation.version !== 1 ||
        !Array.isArray(presentation.tiles) ||
        !Array.isArray(presentation.filters) ||
        !Array.isArray(presentation.columns) ||
        [
          ...presentation.tiles,
          ...presentation.filters,
          ...presentation.columns,
        ].some(
          (item) => !item || typeof item !== 'object' || Array.isArray(item),
        ));
    const unsupportedSettings =
      target &&
      (!target.dashboard.defaults ||
        typeof target.dashboard.defaults !== 'object' ||
        Array.isArray(target.dashboard.defaults) ||
        (target.dashboard.audienceRoleCodes != null &&
          (!Array.isArray(target.dashboard.audienceRoleCodes) ||
            target.dashboard.audienceRoleCodes.some(
              (role) => typeof role !== 'string',
            ))));
    const unavailableReason =
      source.reason ||
      (opaque || unsupportedSettings
        ? 'The saved presentation or settings have an unsupported shape. Their original values are retained.'
        : '');
    return {
      origin,
      dashboard: target?.dashboard ?? null,
      dashboardId: origin.dashboardId,
      label: target?.dashboard.label || 'Dashboard settings',
      sourceLabel: source.label,
      unavailableReason,
      canMutate: !this.dashboardControlsDisabled,
      rows,
      technical:
        this.dashboardTask === 'manage' ? raw : (target?.dashboard ?? raw),
    };
  }
  private settingsCurrent(
    origin: WorkflowDashboardSettingsOrigin | undefined,
    mutation = false,
  ): boolean {
    if (!this.managedDashboardSettings) return !this.settingsDestroyed;
    const view = this.dashboardSettingsView?.() ?? null,
      document = this.sourceDocument(),
      host = this.element.nativeElement;
    return (
      !!origin &&
      !this.settingsDestroyed &&
      this.active &&
      this.scope === 'dashboard' &&
      !!view &&
      !this.ownerViews.focusSuspended() &&
      !host.closest('[hidden],[inert],[aria-hidden="true"]') &&
      view === this.settingsRenderedView &&
      (this.dashboardSettingsViewState === undefined ||
        view === this.dashboardSettingsViewState) &&
      this.settingsAuthority ===
        JSON.stringify([this.removalOwnerKey(), this.removalContext()]) &&
      origin.owner === this.removalOwnerKey() &&
      origin.context === this.removalContext() &&
      origin.view === view &&
      origin.source === JSON.stringify(document) &&
      origin.source === JSON.stringify(this.document) &&
      origin.nodeId === this.node?.id &&
      origin.dashboardId === this.settingsId() &&
      origin.task === this.dashboardTask &&
      origin.mode === this.dashboardMode &&
      origin.generation === this.settingsGeneration &&
      (!mutation ||
        (!this.dashboardControlsDisabled &&
          !this.dashboardSettingsState.unavailableReason))
    );
  }
  private settingsNavigate(
    origin: WorkflowDashboardSettingsOrigin | undefined,
  ): boolean {
    if (
      !this.settingsCurrent(origin) ||
      !this.prepareDashboardChange() ||
      this.beforeSelectionChange?.() === false
    )
      return false;
    return (
      !this.managedDashboardSettings ||
      (origin?.owner === this.removalOwnerKey() &&
        origin.context === this.removalContext() &&
        origin.view === this.dashboardSettingsView?.() &&
        this.active)
    );
  }
  openDashboardSettings(
    id: string,
    origin?: WorkflowDashboardSettingsOrigin,
  ): boolean {
    if (!this.settingsNavigate(origin) || !this.settingsTarget(id))
      return false;
    if (this.dashboardTask === 'canvas')
      this.settingsReturn = {
        dashboardId: this.settingsId(),
        selection: this.selection ? { ...this.selection } : null,
      };
    this.selectedDashboard = id;
    this.dashboardTask = 'settings';
    this.dashboardMode = 'edit';
    this.queueSettingsOpen = true;
    this.inspectorOpen = true;
    this.mobilePanel = 'settings';
    this.responsibilitySettingsOpen = false;
    this.focusDashboardTask();
    return true;
  }
  openDashboardManagement(origin?: WorkflowDashboardSettingsOrigin): boolean {
    if (!this.settingsNavigate(origin)) return false;
    if (this.dashboardTask === 'canvas')
      this.settingsReturn = {
        dashboardId: this.settingsId(),
        selection: this.selection ? { ...this.selection } : null,
      };
    this.dashboardTask = 'manage';
    this.focusDashboardTask();
    return true;
  }
  backToDashboardCanvas(origin?: WorkflowDashboardSettingsOrigin): boolean {
    if (!this.settingsNavigate(origin)) return false;
    const retained = this.settingsReturn;
    if (retained && !this.settingsTarget(retained.dashboardId)) {
      this.error =
        'The previous dashboard is unavailable or ambiguous. Choose an exact dashboard to continue.';
      return false;
    }
    if (retained) {
      this.selectedDashboard = retained.dashboardId;
      this.selection =
        retained.selection &&
        this.document &&
        this.node &&
        this.resolveDashboardRenameTarget(
          this.document,
          this.node.id,
          retained.dashboardId,
          retained.selection,
        )
          ? retained.selection
          : null;
    }
    this.dashboardTask = 'canvas';
    this.settingsReturn = null;
    this.focusDashboardTask('[data-dashboard-settings-entry]');
    return true;
  }
  openManagedDashboard(
    id: string,
    origin: WorkflowDashboardSettingsOrigin,
  ): void {
    if (!this.settingsNavigate(origin) || !this.settingsTarget(id)) return;
    this.selectedDashboard = id;
    this.selection = null;
    this.dashboardTask = 'canvas';
    this.settingsReturn = null;
    this.queueSettingsOpen = false;
    this.inspectorOpen = false;
    this.focusDashboardTask('[data-dashboard-settings-entry]');
  }
  renameManagedDashboard(
    id: string,
    origin: WorkflowDashboardSettingsOrigin,
  ): void {
    if (this.openDashboardSettings(id, origin))
      this.focusDashboardTask('[data-dashboard-rename="dashboard"]', true);
  }
  removeManagedDashboard(
    id: string,
    origin: WorkflowDashboardSettingsOrigin,
  ): void {
    const entered = this.settingsTarget(id),
      reference = JSON.stringify(entered?.dashboard.view);
    if (
      this.dashboardControlsDisabled ||
      !this.settingsCurrent(origin) ||
      !entered ||
      !this.settingsNavigate(origin) ||
      !this.settingsTarget(id)
    )
      return;
    if (reference !== JSON.stringify(this.settingsTarget(id)!.dashboard.view)) {
      this.error =
        'This dashboard source changed while pending input was settling. Review its current identity before removing it.';
      return;
    }
    this.selectedDashboard = id;
    this.removeDashboard(id);
  }
  addManagedDashboard(origin: WorkflowDashboardSettingsOrigin): void {
    if (
      !this.dashboardControlsDisabled &&
      this.settingsCurrent(origin) &&
      this.settingsNavigate(origin)
    )
      this.openDashboardSources();
  }
  manageDashboardInWork(origin?: WorkflowDashboardSettingsOrigin): void {
    if (this.settingsNavigate(origin) && this.workEditorAvailable)
      this.assignmentRequested.emit();
  }
  private focusDashboardTask(
    selector = '[data-dashboard-task-heading]',
    select = false,
  ): void {
    const request = ++this.settingsFocusRequest,
      focus = this.ownerFocusRequest,
      before = this.element.nativeElement.ownerDocument.activeElement;
    const state = this.dashboardSettingsState.origin;
    setTimeout(() => {
      const host = this.element.nativeElement,
        active = host.ownerDocument.activeElement;
      if (
        request !== this.settingsFocusRequest ||
        focus !== this.ownerFocusRequest ||
        !host.isConnected ||
        !this.settingsCurrent(state) ||
        (active !== before &&
          active !== host.ownerDocument.body &&
          active?.isConnected &&
          !active.closest('[hidden],[inert],[aria-hidden="true"]'))
      )
        return;
      const target = host.querySelector<HTMLElement>(selector);
      if (target?.closest('[hidden],[inert],[aria-hidden="true"]')) return;
      target?.focus({ preventScroll: true });
      if (select && target instanceof HTMLInputElement) target.select();
      if (
        host.ownerDocument.activeElement === target &&
        this.settingsCurrent(state)
      )
        target?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    });
  }
  private restoreDashboardModel(
    model: NgModel | undefined,
    value: unknown,
  ): void {
    if (!model || this.restoringDashboardModel) return;
    this.restoringDashboardModel = true;
    try {
      model.control.setValue(value, { emitEvent: false });
    } finally {
      this.restoringDashboardModel = false;
    }
  }
  updateDashboardName(
    value: string,
    origin?: WorkflowDashboardSettingsOrigin,
    model?: NgModel,
  ): void {
    if (this.restoringDashboardModel) return;
    if (this.settingsCurrent(origin, true)) this.updateDashboardLabel(value);
    this.restoreDashboardModel(model, this.dashboardLabelValue);
  }
  commitDashboardName(origin?: WorkflowDashboardSettingsOrigin): boolean {
    return this.settingsCurrent(origin, true) && this.commitDashboardRename();
  }
  dashboardNameKeydown(
    event: Event,
    origin?: WorkflowDashboardSettingsOrigin,
  ): void {
    if (this.settingsCurrent(origin)) this.onDashboardRenameKeydown(event);
  }
  setDashboardSetting(
    kind: string,
    value: string,
    origin?: WorkflowDashboardSettingsOrigin,
    model?: NgModel,
  ): void {
    if (this.restoringDashboardModel) return;
    if (this.settingsCurrent(origin, true)) {
      if (kind === 'status') this.setStatus(value);
      else if (kind === 'audience') this.setAudienceMode(value);
      else if (kind === 'timeZoneId' || kind === 'day')
        this.setHearingDayDefault(kind, value);
      else if (kind === 'queue') this.setQueueScope(value);
      else if (kind === 'all-workflow' || kind === 'all-step')
        this.setIncludedStatuses(
          kind === 'all-workflow' ? 'workflow' : 'step',
          null,
        );
      else if (kind === 'priority-add') this.addPriorityStatus(value);
      else if (kind === 'priority-remove') this.removePriorityStatus(value);
    }
    this.restoreDashboardModel(
      model,
      kind === 'audience'
        ? this.audienceMode
        : kind === 'status'
          ? this.dashboard?.defaults['status'] || ''
          : kind === 'timeZoneId' || kind === 'day'
            ? this.dashboard?.defaults[kind] || ''
            : '',
    );
  }
  toggleDashboardSetting(
    kind: string,
    code: string,
    checked: boolean,
    origin?: WorkflowDashboardSettingsOrigin,
    control?: HTMLInputElement,
  ): void {
    if (this.settingsCurrent(origin, true)) {
      if (kind === 'workflow' || kind === 'step')
        this.toggleIncludedStatus(kind, code, checked);
      else if (kind === 'review') this.toggleReviewWorkStatus(code, checked);
      else if (kind === 'role') this.toggleAudienceRole(code, checked);
    }
    if (control)
      control.checked =
        kind === 'workflow' || kind === 'step'
          ? (this.includedStatusCodes(kind)?.includes(code) ?? false)
          : kind === 'review'
            ? this.reviewWorkStatuses.includes(code)
            : (this.dashboard?.audienceRoleCodes?.includes(code) ?? false);
  }
  moveDashboardPriority(
    index: number,
    offset: number,
    origin?: WorkflowDashboardSettingsOrigin,
  ): void {
    if (this.settingsCurrent(origin, true))
      this.movePriorityStatus(index, offset);
  }
  get dashboardPreviewState() {
    const state = this.dashboardSettingsState,
      target = this.settingsTarget(state.dashboardId);
    const key = target?.dashboard.view?.key;
    const timers = target?.node.declaration?.behavior?.timers;
    const generic =
      key?.provider === 'platform' &&
      key.kind === 'view' &&
      key.code === 'workflow.preparation.work' &&
      key.version === 1;
    const retained = /^1\.(?:[0-9]|1[0-9]|2[01])$/.test(
      target?.document.payloadSchemaVersion ?? '',
    );
    const optionalTimers =
      timers != null &&
      (!Array.isArray(timers) ||
        timers.some(
          (timer) =>
            !timer ||
            typeof timer !== 'object' ||
            Array.isArray(timer) ||
            !('version' in timer) ||
            timer.version !== 1,
        ));
    const reason =
      state.unavailableReason ||
      (!generic
        ? 'Authored-filter reset is available for the exact Step work example only.'
        : !retained || optionalTimers
          ? 'This saved timer or document format has no supported synthetic example. Its original settings are retained.'
          : '');
    return {
      origin: state.origin,
      available: !reason,
      reason,
      actorLabel: 'Example staff member · synthetic actor 1',
    };
  }
  resetDashboardPreview(origin: WorkflowDashboardSettingsOrigin): void {
    if (
      this.dashboardMode !== 'try' ||
      !this.dashboardPreviewState.available ||
      !this.settingsCurrent(origin)
    )
      return;
    const entered = this.settingsTarget(origin.dashboardId),
      reference = JSON.stringify(entered?.dashboard.view);
    if (!entered || !this.settingsNavigate(origin)) return;
    const current = this.settingsTarget(origin.dashboardId);
    if (
      !current ||
      origin.dashboardId !== this.settingsId() ||
      this.dashboardMode !== 'try' ||
      reference !== JSON.stringify(current.dashboard.view) ||
      !this.dashboardPreviewState.available
    ) {
      this.error =
        'This exact dashboard changed. Review its current source before resetting the example.';
      return;
    }
    this.dashboardPreviewResetVersion++;
  }
  private componentChangesPending = false;
  private detailChangesPending = false;
  private loadedDocumentIdentity = '';
  private removalAuthority = '';
  private removalRenderedView: string | null = null;
  private removalEmission: WorkflowDashboardRemovalRequest | null = null;
  sourcePickerOpen = false;
  sourcePickerSelected = '';
  sourcePickerError = '';
  private sourcePickerGeneration = 0;
  private sourceAuthority = '';
  private sourceRenderedView: string | null = null;
  private sourcePickerBaseline = '';
  private sourcePickerOwner = '';
  private sourcePickerView = '';
  private sourcePickerNamespace = '';
  private sourcePickerCatalog = '';
  private sourcePickerSelection = '';
  private sourceEmission: WorkflowDashboardSourceOwnedDocumentChange | null = null;
  private sourceFocus: { document: string; owner: string; context: string; dashboardId: string } | null = null;
  private sourceFocusRequest = 0;
  elementPickerOpen = false;
  elementPickerSelected = '';
  elementPickerError = '';
  readonly elementCategories = [{ id: 'tile', label: 'Summary tile' }, { id: 'filter', label: 'Staff filter' }, { id: 'column', label: 'Table column' }];
  private elementRows: ReturnType<typeof readDashboardElementChoices> = [];
  private elementEntries: readonly WorkflowLibraryEntry[] = [];
  private elementTarget: WorkflowDashboardElementTarget | null = null;
  private elementGeneration = 0;
  private elementAuthority = '';
  private elementRenderedView: string | null = null;
  private elementBaseline = '';
  private elementOwner = '';
  private elementNamespace = '';
  private elementView = '';
  private elementCatalog = '';
  private elementDashboardSelection = '';
  private elementCategory = '';
  private elementEmission: WorkflowDashboardElementOwnedDocumentChange | null = null;
  private elementFocus: { owner: string; context: string; source: string; view: string; target: WorkflowDashboardElementTarget; selection: DashboardSelection } | null = null;
  private elementFocusRequest = 0;
  @ViewChild('dashboardElementLibrary') private dashboardElementLibrary?: WorkflowLibrarySelectorComponent;
  private removalOwnerKey(): string { return JSON.stringify(this.ownerContext?.() ?? null); }
  private removalContext(): string { return JSON.stringify([this.formViewContext?.() ?? null, this.node?.id, this.scope]); }
  /** Logical ownership survives a closed dialog's temporary, not-yet-rendered inert attribute. */
  dashboardRemovalOwnerCurrent(request: WorkflowDashboardRemovalRequest): boolean {
    return this.managedDashboardRemoval && this.active && this.scope === 'dashboard' && !this.dashboardControlsDisabled
      && !!this.dashboardRemovalView && request.view === this.dashboardRemovalView() && request.view === this.removalRenderedView
      && (this.dashboardRemovalViewState === undefined || request.view === this.dashboardRemovalViewState)
      && request.owner === this.removalOwnerKey() && request.context === this.removalContext()
      && this.removalAuthority === JSON.stringify([request.owner, request.context])
      && request.nodeId === this.node?.id && request.selection === this.selectedDashboard;
  }
  isCurrentDashboardRemovalRequest(request: WorkflowDashboardRemovalRequest): boolean {
    const latest = this.latestDocument?.() ?? this.document;
    return !this.elementPickerOpen && this.removalEmission === request && this.dashboardRemovalOwnerCurrent(request)
      && !this.ownerViews.focusSuspended() && !this.element.nativeElement.closest('[hidden],[inert],[aria-hidden="true"]')
      && request.source === JSON.stringify(latest) && request.source === JSON.stringify(this.document);
  }
  dashboardRemovalLabelScopes(request: WorkflowDashboardRemovalRequest): readonly WorkflowEditorPendingScope[] {
    if (this.scope !== 'dashboard' || this.node?.id !== request.nodeId || this.dashboardRename
      && (this.dashboardRename.nodeId !== request.nodeId || this.dashboardRename.dashboardId !== request.dashboardId)) return [];
    return this.pendingEditorScopes(() => this.latestDocument?.() ?? this.document)
      .filter(scope => scope.id === `${request.nodeId}:dashboard-name` || scope.id === `${request.nodeId}:dashboard-element-label`);
  }
  private sourceDocument(): WorkflowDefinitionDocument | null { return this.latestDocument ? this.latestDocument() : this.document; }
  sourceIdentity(reference: WorkflowCapabilityReference): string {
    return JSON.stringify([reference?.key?.provider, reference?.key?.kind, reference?.key?.code, reference?.key?.version,
      reference?.inputSchemaHash, reference?.outputSchemaHash, reference?.contractHash]);
  }
  private sourceLogicalCurrent(): boolean {
    const document = this.sourceDocument(), view = this.dashboardSourceView?.() ?? null;
    return this.managedDashboardSources && !!document && this.active && this.scope === 'dashboard' && !this.dashboardControlsDisabled
      && !!view && view === this.sourceRenderedView && (this.dashboardSourceViewState === undefined || view === this.dashboardSourceViewState)
      && this.sourceAuthority === JSON.stringify([this.removalOwnerKey(), this.removalContext()])
      && JSON.stringify(document) === JSON.stringify(this.document);
  }
  get sourcePickerContext(): string {
    return JSON.stringify([this.sourcePickerGeneration, this.removalOwnerKey(), this.removalContext(), this.sourceDocument(),
      this.dashboardSourceView?.() ?? null, this.componentCatalog, this.componentCapabilities]);
  }
  get sourcePickerActive(): boolean {
    // The selector's own CDK dialog hides the application from accessibility APIs.
    return this.sourceLogicalCurrent() && !this.element.nativeElement.closest('[hidden],[inert]');
  }
  private sourcePickerCurrent(): boolean {
    return this.sourcePickerOpen && this.sourcePickerActive && !this.hasPendingChanges()
      && this.sourcePickerOwner === this.removalOwnerKey() && this.sourcePickerNamespace === this.removalContext()
      && this.sourcePickerBaseline === JSON.stringify(this.sourceDocument()) && this.sourcePickerView === this.dashboardSourceView?.()
      && this.sourcePickerSelection === this.selectedDashboard && this.sourcePickerCatalog === JSON.stringify([this.componentCatalog, this.componentCapabilities]);
  }
  sourceChoice(id: string): StepAuthoringView | null {
    const matches = Array.isArray(this.componentCatalog?.views) ? this.componentCatalog.views.filter(view => view && this.sourceIdentity(view.capability) === id) : [];
    return matches.length === 1 ? matches[0] : null;
  }
  private sourceReason(view: StepAuthoringView): string {
    if (!this.componentsEditor) return 'Dashboard source support is unavailable.';
    try { return this.componentsEditor.dashboardSourceReason(view.capability, this.sourceDocument()) ?? ''; }
    catch { return 'This exact dashboard source is unavailable. Its settings have not been changed.'; }
  }
  get sourcePickerEntries(): readonly WorkflowLibraryEntry[] {
    if (!this.sourcePickerOpen) return [];
    return (Array.isArray(this.componentCatalog?.views) ? this.componentCatalog.views : []).filter(Boolean).map(view => ({
      id: this.sourceIdentity(view.capability), label: typeof view.label === 'string' ? view.label : 'Unavailable source',
      description: typeof view.description === 'string' ? view.description : 'Source details unavailable.',
      categoryId: 'source', actionLabel: 'Add dashboard', disabledReason: this.sourceReason(view),
    }));
  }
  get sourcePickerChoice(): StepAuthoringView | null {
    if (!this.sourcePickerOpen) return null;
    const choice = this.sourceChoice(this.sourcePickerSelected); return choice && !this.sourceReason(choice) ? choice : null;
  }
  openDashboardSourcePicker(): void {
    if (this.elementPickerOpen) return;
    if (!this.sourceLogicalCurrent() || this.ownerViews.focusSuspended() || this.element.nativeElement.closest('[hidden],[inert],[aria-hidden="true"]')) return;
    if (this.hasPendingChanges()) { this.error = 'Finish the current dashboard input before choosing a source. Your input is retained.'; this.focusPendingChanges(); return; }
    this.sourcePickerBaseline = JSON.stringify(this.sourceDocument()); this.sourcePickerOwner = this.removalOwnerKey();
    this.sourcePickerView = this.dashboardSourceView!()!; this.sourcePickerNamespace = this.removalContext();
    this.sourcePickerSelection = this.selectedDashboard; this.sourcePickerCatalog = JSON.stringify([this.componentCatalog, this.componentCapabilities]);
    this.sourcePickerSelected = ''; this.sourcePickerError = ''; this.sourceFocus = null;
    this.sourcePickerGeneration++; this.sourcePickerOpen = true;
  }
  openDashboardSources(): void {
    if (this.managedDashboardSources) this.openDashboardSourcePicker();
    else this.showSources = !this.showSources;
  }
  reviewDashboardSource(id: string): void {
    const choice = this.sourceChoice(id);
    if (this.sourcePickerCurrent() && choice && !this.sourceReason(choice)) this.sourcePickerSelected = id;
  }
  chooseDashboardSource(id: string): void {
    this.reviewDashboardSource(id);
    if (this.sourcePickerSelected === id) this.configureDashboardSource(this.sourcePickerContext);
  }
  isCurrentDashboardSourceChange(event: WorkflowDashboardSourceOwnedDocumentChange, current: WorkflowDefinitionDocument): boolean {
    if (this.sourceEmission !== event || !this.sourcePickerCurrent() || !this.componentsEditor || event.nodeId !== this.node?.id
      || event.owner !== this.removalOwnerKey() || event.source !== JSON.stringify(current) || event.view !== this.sourcePickerView
      || event.context !== this.sourcePickerContext || this.sourceIdentity(event.reference) !== this.sourcePickerSelected) return false;
    try { return JSON.stringify(this.componentsEditor.proposeDashboardSource(event.reference, current)) === JSON.stringify(event.document); }
    catch { return false; }
  }
  configureDashboardSource(context: string): void {
    const choice = this.sourcePickerChoice, current = this.sourceDocument(), nodeId = this.node?.id;
    if (!this.sourcePickerCurrent() || context !== this.sourcePickerContext || !choice || !current || !nodeId || !this.componentsEditor || this.sourceEmission) return;
    try {
      const next = this.componentsEditor.proposeDashboardSource(choice.capability, current), owner = this.removalOwnerKey(), namespace = this.removalContext();
      const existing = new Set(current.nodes.filter(node => node.id === nodeId)[0]?.declaration?.dashboards?.map(dashboard => dashboard.id));
      const nodes = next.nodes.filter(node => node.id === nodeId), added = nodes.length === 1 ? nodes[0].declaration?.dashboards?.filter(dashboard => !existing.has(dashboard.id)) : [];
      if (added?.length !== 1) throw new Error('The selected source did not produce one exact dashboard. No dashboard was added.');
      const event: WorkflowDashboardSourceOwnedDocumentChange = { document: next, nodeId, reference: structuredClone(choice.capability), owner,
        source: JSON.stringify(current), view: this.sourcePickerView, context };
      this.sourceEmission = event; try { this.dashboardSourceOwnedDocumentChange.emit(event); } finally { this.sourceEmission = null; }
      const receipt = this.sourceDocument();
      if (!receipt || receipt === current || owner !== this.removalOwnerKey() || namespace !== this.removalContext()
        || this.sourcePickerView !== this.dashboardSourceView?.() || this.sourcePickerSelection !== this.selectedDashboard
        || this.sourcePickerSelected !== this.sourceIdentity(choice.capability) || this.sourcePickerCatalog !== JSON.stringify([this.componentCatalog, this.componentCapabilities])
        || JSON.stringify(next) !== JSON.stringify(receipt)) {
        this.sourcePickerError = 'The dashboard was not added to the current draft. Your source selection is retained.'; return;
      }
      this.document = receipt; this.node = receipt.nodes.filter(node => node.id === nodeId)[0]; this.selectedDashboard = added[0].id;
      this.dashboardTask = 'canvas'; this.settingsReturn = null;
      this.selection = null; this.queueSettingsOpen = false; this.inspectorOpen = false; this.mobilePanel = 'page'; this.dashboardMode = 'edit';
      this.sourceFocus = { document: JSON.stringify(receipt), owner, context: namespace, dashboardId: added[0].id };
      this.sourcePickerOpen = false; this.sourcePickerError = '';
    } catch (error) { this.sourcePickerError = error instanceof Error ? error.message : 'The dashboard could not be added. Existing settings are retained.'; }
  }
  dashboardSourcePickerClosed(): void {
    this.sourcePickerOpen = false;
    const focus = this.sourceFocus; this.sourceFocus = null;
    if (!focus) return;
    const request = ++this.sourceFocusRequest, before = this.element.nativeElement.ownerDocument.activeElement;
    setTimeout(() => {
      const host = this.element.nativeElement, active = host.ownerDocument.activeElement;
      if (request !== this.sourceFocusRequest || !host.isConnected || !this.sourceLogicalCurrent() || this.ownerViews.focusSuspended()
        || host.closest('[hidden],[inert],[aria-hidden="true"]') || focus.owner !== this.removalOwnerKey() || focus.context !== this.removalContext()
        || focus.document !== JSON.stringify(this.sourceDocument()) || this.selectedDashboard !== focus.dashboardId || this.sourcePickerOpen) return;
      if (active !== before && active !== host.ownerDocument.body && active?.isConnected && !active.closest('[hidden],[inert],[aria-hidden="true"]')) return;
      const heading = host.querySelector<HTMLElement>('[data-dashboard-canvas-heading]');
      if (heading?.closest('[hidden],[inert],[aria-hidden="true"]')) return;
      heading?.focus({ preventScroll: true });
      if (host.ownerDocument.activeElement === heading && focus.owner === this.removalOwnerKey() && focus.document === JSON.stringify(this.sourceDocument())) heading?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    });
  }
  private elementLogicalCurrent(): boolean {
    const view = this.dashboardElementView?.() ?? null;
    return this.managedDashboardElements && this.active && this.scope === 'dashboard' && !this.dashboardControlsDisabled
      && !!view && view === this.elementRenderedView && (this.dashboardElementViewState === undefined || view === this.dashboardElementViewState)
      && this.elementAuthority === JSON.stringify([this.removalOwnerKey(), this.removalContext()])
      && JSON.stringify(this.sourceDocument()) === JSON.stringify(this.document);
  }
  get elementPickerActive(): boolean {
    return this.elementLogicalCurrent() && !this.element.nativeElement.closest('[hidden],[inert]');
  }
  get elementPickerContext(): string {
    return JSON.stringify([this.elementGeneration,
      this.removalOwnerKey(), this.removalContext(), this.sourceDocument(), this.dashboardElementView?.(), this.selectedDashboard,
      this.componentCatalog, this.componentCapabilities, this.elementTarget]);
  }
  private elementPickerCurrent(): boolean {
    return this.elementPickerOpen && this.elementPickerActive && !this.hasPendingChanges() && this.elementOwner === this.removalOwnerKey()
      && this.elementNamespace === this.removalContext() && this.elementView === this.dashboardElementView?.()
      && this.elementDashboardSelection === this.selectedDashboard && this.elementBaseline === JSON.stringify(this.sourceDocument())
      && this.elementCatalog === JSON.stringify([this.componentCatalog, this.componentCapabilities]);
  }
  get elementPickerEntries(): readonly WorkflowLibraryEntry[] { return this.elementPickerOpen ? this.elementEntries : []; }
  get elementPickerChoice(): ReturnType<typeof readDashboardElementChoices>[number] | null {
    const rows = this.elementPickerOpen ? this.elementRows.filter(row => row.id === this.elementPickerSelected) : [];
    return rows.length === 1 ? rows[0] : null;
  }
  requestDashboardElements(dashboardId: string, category = ''): void {
    if (this.managedDashboardElements) this.openDashboardElements(dashboardId, category);
    else this.addToolsOpen = !this.addToolsOpen;
  }
  openDashboardElements(dashboardId: string, category = ''): void {
    if (!this.elementLogicalCurrent() || this.sourcePickerOpen || this.elementPickerOpen || this.ownerViews.focusSuspended()
      || this.element.nativeElement.closest('[hidden],[inert],[aria-hidden="true"]')) return;
    if (this.hasPendingChanges()) { this.error = 'Finish the current dashboard input before choosing an element. Your input is retained.'; this.focusPendingChanges(); return; }
    const document = this.sourceDocument(), nodeId = this.node?.id;
    if (!document || !nodeId || JSON.stringify(document) !== JSON.stringify(this.document) || !this.componentCatalog) return;
    const nodes = Array.isArray(document.nodes) ? document.nodes.filter(node => node?.id === nodeId) : [];
    const dashboards = nodes.length === 1 && nodes[0].typeCode === 'human_step' && Array.isArray(nodes[0].declaration?.dashboards)
      ? nodes[0].declaration.dashboards.filter(dashboard => dashboard?.id === dashboardId) : [];
    if (dashboards.length !== 1 || this.selectedDashboard && this.selectedDashboard !== dashboardId) { this.error = 'Select one exact saved dashboard before adding an element.'; return; }
    this.elementTarget = { nodeId, dashboardId, reference: structuredClone(dashboards[0].view) };
    this.elementBaseline = JSON.stringify(document); this.elementOwner = this.removalOwnerKey(); this.elementNamespace = this.removalContext();
    this.elementView = this.dashboardElementView!()!; this.elementDashboardSelection = this.selectedDashboard;
    this.elementCatalog = JSON.stringify([this.componentCatalog, this.componentCapabilities]);
    this.elementPickerSelected = ''; this.elementPickerError = ''; this.elementFocus = null; this.elementGeneration++;
    this.elementCategory = this.elementCategories.some(item => item.id === category) ? category : '';
    try {
      this.elementRows = readDashboardElementChoices(document, this.elementTarget, this.componentCatalog, this.componentCapabilities);
      this.elementEntries = this.elementRows.map(row => ({ id: row.id, label: row.label, description: row.description,
        categoryId: row.category, disabledReason: row.reason ?? '', actionLabel: 'Review element',
        ...(row.existing.length ? { ownerAction: { id: 'inspect-existing', label: row.existing.length === 1 ? 'Inspect existing' : 'Review existing elements' } } : {}) }));
    } catch (error) { this.elementRows = []; this.elementEntries = []; this.elementPickerError = error instanceof Error ? error.message : 'Dashboard elements are unavailable. Existing settings are retained.'; }
    this.elementPickerOpen = true;
    this.dashboardElementLibrary?.category.set(this.elementCategory);
  }
  dashboardElementPickerOpened(): void { this.dashboardElementLibrary?.category.set(this.elementCategory); }
  reviewDashboardElement(id: string): void {
    if (this.elementPickerCurrent() && this.elementRows.filter(row => row.id === id && !row.reason).length === 1) { this.elementPickerSelected = id; this.elementPickerError = ''; }
  }
  inspectDashboardElementChoice(request: { id: string; actionId: string }): void {
    if (!this.elementPickerCurrent() || request.actionId !== 'inspect-existing') return;
    const rows = this.elementRows.filter(row => row.id === request.id);
    if (rows.length !== 1) return;
    this.elementPickerSelected = request.id;
    if (rows[0].existing.length === 1) this.inspectExistingDashboardElement(rows[0].existing[0].selection);
  }
  inspectExistingDashboardElement(selection: DashboardSelection): void {
    const document = this.sourceDocument(), target = this.elementTarget;
    if (!this.elementPickerCurrent() || !document || !target || !this.componentCatalog) return;
    try {
      const rows = readDashboardElementChoices(document, target, this.componentCatalog, this.componentCapabilities);
      const row = rows.filter(item => item.id === this.elementPickerSelected);
      const existing = row.length === 1 ? row[0].existing.filter(item => item.selection.kind === selection.kind && item.selection.id === selection.id) : [];
      if (existing.length !== 1) throw new Error('This existing element is missing or ambiguous. Its saved settings are retained.');
      this.selectedDashboard = target.dashboardId; this.selection = { ...selection }; this.queueSettingsOpen = false; this.inspectorOpen = true; this.responsibilitySettingsOpen = false; this.mobilePanel = 'settings';
      this.elementFocus = { owner: this.elementOwner, context: this.elementNamespace, source: JSON.stringify(document), view: this.elementView,
        target: structuredClone(target), selection: { ...selection } };
      this.elementPickerOpen = false;
    } catch (error) { this.elementPickerError = error instanceof Error ? error.message : 'The saved element could not be inspected.'; }
  }
  isCurrentDashboardElementChange(event: WorkflowDashboardElementOwnedDocumentChange, current: WorkflowDefinitionDocument): boolean {
    if (this.elementEmission !== event || !this.elementPickerCurrent() || !this.elementTarget || !this.componentCatalog
      || event.owner !== this.removalOwnerKey() || event.source !== JSON.stringify(current) || event.context !== this.elementPickerContext
      || event.view !== this.elementView || event.nodeId !== this.elementTarget.nodeId || event.dashboardId !== this.elementTarget.dashboardId
      || !sameCapabilityReference(event.reference, this.elementTarget.reference)) return false;
    const row = this.elementPickerChoice;
    if (!row || row.reason || JSON.stringify(row.choice) !== JSON.stringify(event.choice)) return false;
    try {
      const result = applyDashboardElement(current, this.elementTarget, this.componentCatalog, this.componentCapabilities, event.choice);
      return JSON.stringify(result.document) === JSON.stringify(event.document) && JSON.stringify(result.selection) === JSON.stringify(event.selection);
    } catch { return false; }
  }
  configureDashboardElement(context: string): void {
    const row = this.elementPickerChoice, document = this.sourceDocument(), target = this.elementTarget;
    if (!this.elementPickerCurrent() || !row || row.reason || !document || !target || !this.componentCatalog || this.elementEmission || context !== this.elementPickerContext) return;
    try {
      const result = applyDashboardElement(document, target, this.componentCatalog, this.componentCapabilities, row.choice);
      const event: WorkflowDashboardElementOwnedDocumentChange = { document: result.document, ...target, choice: structuredClone(row.choice),
        selection: { ...result.selection }, owner: this.elementOwner, source: JSON.stringify(document), view: this.elementView, context };
      this.elementEmission = event; try { this.dashboardElementOwnedDocumentChange.emit(event); } finally { this.elementEmission = null; }
      const receipt = this.sourceDocument();
      if (!receipt || receipt === document || this.elementOwner !== this.removalOwnerKey() || this.elementNamespace !== this.removalContext()
        || this.elementView !== this.dashboardElementView?.() || this.elementDashboardSelection !== this.selectedDashboard
        || this.elementCatalog !== JSON.stringify([this.componentCatalog, this.componentCapabilities])
        || JSON.stringify(receipt) !== JSON.stringify(result.document)) {
        this.elementPickerError = 'The element was not added to the current draft. Your selection is retained.'; return;
      }
      this.document = receipt; this.node = receipt.nodes.filter(node => node.id === target.nodeId)[0]; this.selection = { ...result.selection };
      this.selectedDashboard = target.dashboardId; this.queueSettingsOpen = false; this.inspectorOpen = true; this.mobilePanel = 'settings'; this.responsibilitySettingsOpen = false;
      this.elementFocus = { owner: this.elementOwner, context: this.elementNamespace, source: JSON.stringify(receipt), view: this.elementView,
        target: structuredClone(target), selection: { ...result.selection } };
      this.elementPickerOpen = false; this.elementPickerError = '';
    } catch (error) { this.elementPickerError = error instanceof Error ? error.message : 'The element could not be added. Existing settings are retained.'; }
  }
  dashboardElementPickerClosed(): void {
    this.elementPickerOpen = false; this.elementRows = []; this.elementEntries = [];
    const focus = this.elementFocus; this.elementFocus = null;
    if (!focus) return;
    const request = ++this.elementFocusRequest, before = this.element.nativeElement.ownerDocument.activeElement;
    setTimeout(() => {
      const host = this.element.nativeElement, active = host.ownerDocument.activeElement;
      if (request !== this.elementFocusRequest || !host.isConnected || !this.elementLogicalCurrent() || this.ownerViews.focusSuspended()
        || host.closest('[hidden],[inert],[aria-hidden="true"]') || this.elementPickerOpen || focus.owner !== this.removalOwnerKey()
        || focus.context !== this.removalContext() || focus.source !== JSON.stringify(this.sourceDocument()) || focus.view !== this.dashboardElementView?.()
        || focus.target.dashboardId !== this.selectedDashboard || JSON.stringify(focus.selection) !== JSON.stringify(this.selection)) return;
      if (active !== before && active !== host.ownerDocument.body && active?.isConnected && !active.closest('[hidden],[inert],[aria-hidden="true"]')) return;
      const target = this.dashboardInspector?.nativeElement.querySelector<HTMLElement>('[data-dashboard-rename="item"]')
        ?? this.dashboardInspector?.nativeElement.querySelector<HTMLElement>('[data-dashboard-heading]');
      target?.focus({ preventScroll: true });
      if (request === this.elementFocusRequest && host.ownerDocument.activeElement === target && this.elementLogicalCurrent() && !this.elementPickerOpen
        && !this.ownerViews.focusSuspended() && !host.closest('[hidden],[inert],[aria-hidden="true"]')
        && focus.owner === this.removalOwnerKey() && focus.source === JSON.stringify(this.sourceDocument())
        && focus.view === this.dashboardElementView?.() && JSON.stringify(focus.selection) === JSON.stringify(this.selection)) target?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    });
  }
  get dashboardLabelValue(): string {
    return this.dashboardRename && !this.dashboardRename.target
      ? this.dashboardRename.value
      : (this.dashboard?.label ?? '');
  }
  get selectedItemLabelValue(): string {
    return this.dashboardRename?.target
      ? this.dashboardRename.value
      : (this.selectedItem?.label ?? '');
  }
  get dashboardLabelError(): string {
    return this.dashboardRename && !this.dashboardRename.target
      ? this.dashboardRename.error
      : '';
  }
  get selectedItemLabelError(): string {
    return this.dashboardRename?.target ? this.dashboardRename.error : '';
  }
  hasDashboardRename(): boolean {
    return this.dashboardRename !== null;
  }
  hasPendingChanges(): boolean {
    return (
      this.hasDashboardRename() ||
      this.componentsEditor?.hasPendingChanges() === true ||
      this.detailComposer?.hasPendingChanges() === true
    );
  }
  /** Export nested route input as inert recovery text, including paths outside the selected detail. */
  capturePendingRouteInputs(): readonly { name: string; label: string; value: string }[] {
    return this.componentsEditor?.capturePendingRouteInputs?.() ?? [];
  }
  /** Keep label, Staff text and nested action/routing input independently guarded. */
  pendingEditorScopes(
    latestDocument: () => WorkflowDefinitionDocument | null = () => this.document,
  ): readonly WorkflowEditorPendingScope[] {
    const labels: WorkflowEditorPendingScope[] = this.scope === 'dashboard'
      ? (['dashboard', 'element'] as const).map(kind => {
        const draft = () => this.dashboardRename &&
          (kind === 'element' ? this.dashboardRename.target !== null : this.dashboardRename.target === null)
          ? this.dashboardRename : null;
        const current = () => {
          const buffered = draft(), document = latestDocument();
          return buffered && document
            ? this.resolveDashboardRenameTarget(document, buffered.nodeId, buffered.dashboardId, buffered.target) : null;
        };
        return {
          id: `${this.node?.id ?? 'unavailable'}:${kind === 'dashboard' ? 'dashboard-name' : 'dashboard-element-label'}`,
          label: kind === 'dashboard' ? 'Dashboard name' : 'Dashboard element label',
          state: (): WorkflowEditorBufferState => {
            const buffered = draft();
            if (!buffered) return 'clean';
            const document = latestDocument(), source = current();
            if (!document || !source || this.scope !== 'dashboard' ||
              this.node?.id !== buffered.nodeId || document.workflowCode !== buffered.workflowCode ||
              document.definitionVersionCode !== buffered.definitionVersionCode ||
              JSON.stringify(document.packReference ?? null) !== buffered.packReference ||
              source.node.typeCode !== buffered.nodeType ||
              !sameCapabilityReference(source.dashboard.view, buffered.view) || source.label !== buffered.originalLabel) return 'stale';
            if (this.managedDashboardSettings && (buffered.owner !== this.removalOwnerKey() || buffered.context !== this.removalContext())) return 'stale';
            return this.dashboardControlsDisabled || this.dashboardLabelValidation(buffered.value) ? 'invalid' : 'valid';
          },
          fingerprint: () => {
            const buffered = draft(), document = latestDocument(), source = current();
            return JSON.stringify({
              draft: buffered,
              owner: [this.node?.id, this.scope, this.resetVersion],
              context: document ? [document.workflowCode, document.definitionVersionCode, document.packReference] : null,
              source: source ? [source.node.typeCode, source.dashboard.view, source.label] : null,
              editable: [this.disabled, this.loading, this.queueChangesPending, this.dashboardMode],
            });
          },
          apply: () => {
            if (!draft()) return true;
            const document = latestDocument();
            return !!document && this.commitDashboardRename(document);
          },
          discard: () => {
            if (!draft()) return;
            this.dashboardRename = null;
            this.emitPendingChanges();
          },
          focus: () => { if (draft()) this.focusDashboardRename(kind === 'element' ? 'item' : 'dashboard'); },
        };
      }) : [];
    return [
      ...labels,
      ...(this.detailComposer?.pendingEditorScopes(latestDocument) ?? []),
      ...(this.componentsEditor?.pendingEditorScopes(latestDocument) ?? []),
    ];
  }
  onComponentPendingChanges(pending: boolean): void {
    const changed = pending !== this.componentChangesPending;
    this.componentChangesPending = pending;
    if (changed || this.hasDashboardRename()) this.emitPendingChanges();
  }
  onDetailPendingChanges(pending: boolean): void {
    const changed = pending !== this.detailChangesPending;
    this.detailChangesPending = pending;
    if (changed || this.hasDashboardRename() || this.componentChangesPending)
      this.emitPendingChanges();
  }
  updateDashboardLabel(value: string): void {
    this.updateDashboardRename(null, value);
  }
  updateItemLabel(value: string): void {
    if (this.selection) this.updateDashboardRename(this.selection, value);
  }
  private updateDashboardRename(
    target: DashboardSelection | null,
    value: string,
  ): void {
    if (
      this.scope !== 'dashboard' ||
      this.dashboardControlsDisabled ||
      !this.document ||
      !this.node ||
      !this.supportedDashboard ||
      !this.dashboard
    )
      return;
    if (
      this.dashboardRename &&
      JSON.stringify(this.dashboardRename.target) !== JSON.stringify(target) &&
      !this.commitDashboardRename()
    )
      return;
    if (!this.dashboardRename) {
      const current = this.resolveDashboardRenameTarget(
        this.document,
        this.node.id,
        this.dashboard.id,
        target,
      );
      if (!current) return;
      this.dashboardRename = {
        workflowCode: this.document.workflowCode,
        definitionVersionCode: this.document.definitionVersionCode,
        packReference: JSON.stringify(this.document.packReference ?? null),
        nodeId: this.node.id,
        nodeType: this.node.typeCode,
        dashboardId: this.dashboard.id,
        view: structuredClone(this.dashboard.view),
        target: target ? { ...target } : null,
        originalLabel: current.label,
        value,
        error: '',
        ...(this.managedDashboardSettings ? { owner: this.removalOwnerKey(), context: this.removalContext() } : {}),
      };
    }
    this.dashboardRename.value = value;
    this.dashboardRename.error = this.dashboardLabelValidation(value);
    if (value === this.dashboardRename.originalLabel)
      this.dashboardRename = null;
    this.emitPendingChanges();
  }
  commitDashboardRename(latestDocument?: WorkflowDefinitionDocument): boolean {
    const rename = this.dashboardRename;
    if (!rename) return true;
    if (this.managedDashboardSettings) return this.commitOwnedDashboardName(latestDocument ?? this.sourceDocument());
    if (this.scope !== 'dashboard' || this.dashboardControlsDisabled)
      return this.rejectDashboardRename(
        'This label cannot be applied while dashboard editing is unavailable.',
      );
    const latest = latestDocument ?? this.document;
    const current = latest
      ? this.resolveDashboardRenameTarget(
          latest,
          rename.nodeId,
          rename.dashboardId,
          rename.target,
        )
      : null;
    if (
      !latest ||
      !current ||
      latest.workflowCode !== rename.workflowCode ||
      latest.definitionVersionCode !== rename.definitionVersionCode ||
      JSON.stringify(latest.packReference ?? null) !== rename.packReference ||
      current.node.typeCode !== rename.nodeType ||
      !sameCapabilityReference(current.dashboard.view, rename.view) ||
      current.label !== rename.originalLabel
    )
      return this.rejectDashboardRename(
        'This saved label or its dashboard changed while you were editing. Cancel the rename and review the current settings.',
      );
    const error = this.dashboardLabelValidation(rename.value);
    if (error) return this.rejectDashboardRename(error);
    const label = rename.value.trim();
    this.document = latest;
    this.node = current.node;
    if (label === current.label) {
      this.dashboardRename = null;
      this.emitPendingChanges();
      return true;
    }
    let dashboard = current.dashboard;
    if (!rename.target) dashboard = { ...dashboard, label };
    else {
      const presentation = current.presentation!;
      const key =
        rename.target.kind === 'tile'
          ? 'tiles'
          : rename.target.kind === 'filter'
            ? 'filters'
            : 'columns';
      const id = rename.target.id;
      dashboard = {
        ...dashboard,
        presentation: {
          ...presentation,
          [key]: presentation[key].map((item) =>
            ('id' in item ? item.id : item.field) === id
              ? { ...item, label }
              : item,
          ),
        },
      };
    }
    const updated = replacePresentation(latest, current.node.id, {
      ...current.node.declaration!,
      dashboards: current.node.declaration!.dashboards!.map((item) =>
        item.id === rename.dashboardId ? dashboard : item,
      ),
    });
    this.document = updated;
    this.node = updated.nodes.find((item) => item.id === rename.nodeId)!;
    this.dashboardRename = null;
    this.emitPendingChanges();
    this.documentChange.emit(updated);
    return true;
  }
  private dashboardNameProposal(
    document: WorkflowDefinitionDocument,
    rename: WorkflowDashboardNameDraft,
  ): WorkflowDefinitionDocument | null {
    const current = this.resolveDashboardRenameTarget(
      document,
      rename.nodeId,
      rename.dashboardId,
      rename.target,
    );
    if (
      !current ||
      current.node.typeCode !== 'human_step' ||
      current.node.typeCode !== rename.nodeType ||
      document.workflowCode !== rename.workflowCode ||
      document.definitionVersionCode !== rename.definitionVersionCode ||
      JSON.stringify(document.packReference ?? null) !== rename.packReference ||
      !sameCapabilityReference(current.dashboard.view, rename.view) ||
      current.label !== rename.originalLabel ||
      this.dashboardLabelValidation(rename.value)
    )
      return null;
    const label = rename.value.trim();
    if (label === current.label) return document;
    let dashboard = current.dashboard;
    if (!rename.target) dashboard = { ...dashboard, label };
    else {
      const key =
        rename.target.kind === 'tile'
          ? 'tiles'
          : rename.target.kind === 'filter'
            ? 'filters'
            : 'columns';
      dashboard = {
        ...current.dashboard,
        presentation: {
          ...current.presentation!,
          [key]: current.presentation![key].map((item) =>
            ('id' in item ? item.id : item.field) === rename.target!.id
              ? { ...item, label }
              : item,
          ),
        },
      };
    }
    return replacePresentation(document, current.node.id, {
      ...current.node.declaration!,
      dashboards: current.node.declaration!.dashboards!.map((item) =>
        item.id === rename.dashboardId ? dashboard : item,
      ),
    });
  }
  isCurrentDashboardNameChange(
    event: WorkflowDashboardNameOwnedDocumentChange,
    current: WorkflowDefinitionDocument,
  ): boolean {
    const draft = this.dashboardRename;
    if (
      this.nameEmission !== event ||
      !draft ||
      !this.managedDashboardSettings ||
      this.dashboardControlsDisabled ||
      event.owner !== this.removalOwnerKey() ||
      event.context !== this.removalContext() ||
      event.source !== JSON.stringify(current) ||
      event.nodeId !== this.node?.id ||
      event.dashboardId !== draft.dashboardId ||
      draft.owner !== event.owner ||
      draft.context !== event.context ||
      JSON.stringify(event.draft) !== JSON.stringify(draft) ||
      !sameCapabilityReference(event.reference, draft.view) ||
      event.view !== (this.dashboardSettingsView?.() ?? null) ||
      (!this.dashboardNameSettlement?.() &&
        !this.settingsCurrent(this.dashboardSettingsState.origin, true))
    )
      return false;
    const expected = this.dashboardNameProposal(current, draft);
    return (
      !!expected && JSON.stringify(expected) === JSON.stringify(event.document)
    );
  }
  private commitOwnedDashboardName(
    current: WorkflowDefinitionDocument | null,
  ): boolean {
    const draft = this.dashboardRename;
    if (!draft) return true;
    const pending = structuredClone(draft),
      owner = this.removalOwnerKey(),
      context = this.removalContext();
    if (
      !current ||
      this.dashboardControlsDisabled ||
      draft.owner !== owner ||
      draft.context !== context ||
      (!this.dashboardNameSettlement?.() &&
        !this.settingsCurrent(this.dashboardSettingsState.origin, true))
    )
      return this.rejectDashboardRename(
        'This pending label belongs to an unavailable owner. Your input is retained; cancel it to review the current settings.',
      );
    const expected = this.dashboardNameProposal(current, draft);
    if (!expected)
      return this.rejectDashboardRename(
        this.dashboardLabelValidation(draft.value) ||
          'This saved label or its exact dashboard changed. Your input is retained; cancel it and review the current settings.',
      );
    const event: WorkflowDashboardNameOwnedDocumentChange = {
      document: expected,
      nodeId: draft.nodeId,
      dashboardId: draft.dashboardId,
      reference: structuredClone(draft.view),
      owner,
      source: JSON.stringify(current),
      view: this.dashboardSettingsView?.() ?? null,
      context,
      draft: pending,
    };
    this.nameEmission = event;
    try {
      this.dashboardNameOwnedDocumentChange.emit(event);
    } finally {
      this.nameEmission = null;
    }
    const receipt = this.sourceDocument();
    if (
      !receipt ||
      receipt === current ||
      owner !== this.removalOwnerKey() ||
      context !== this.removalContext() ||
      JSON.stringify(this.dashboardRename) !== JSON.stringify(pending) ||
      JSON.stringify(receipt) !== JSON.stringify(expected)
    )
      return this.rejectDashboardRename(
        'The current draft did not accept this label. Your input is retained.',
      );
    this.document = receipt;
    this.node = receipt.nodes.filter((node) => node.id === pending.nodeId)[0];
    this.dashboardRename = null;
    this.emitPendingChanges();
    return true;
  }
  cancelDashboardRename(): void {
    const target = this.dashboardRename?.target ? 'item' : 'dashboard';
    this.dashboardRename = null;
    this.emitPendingChanges();
    this.focusDashboardRename(target);
  }
  onDashboardRenameKeydown(event: Event): void {
    const key = (event as KeyboardEvent).key;
    if (key === 'Enter') {
      event.preventDefault();
      this.commitDashboardRename();
    } else if (key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.cancelDashboardRename();
    }
  }
  editDashboardName(): void {
    if (this.prepareDashboardChange() && this.openQueueSettings())
      this.focusDashboardRename('dashboard', true);
  }
  private resolveDashboardRenameTarget(
    document: WorkflowDefinitionDocument,
    nodeId: string,
    dashboardId: string,
    target: DashboardSelection | null,
  ): {
    node: WorkflowDefinitionNode;
    dashboard: DashboardView;
    label: string;
    presentation: DashboardPresentation | null;
  } | null {
    try {
      const nodes = document.nodes.filter((node) => node?.id === nodeId);
      if (nodes.length !== 1) return null;
      const node = nodes[0];
      const dashboards =
        node.declaration?.dashboards?.filter(
          (dashboard) => dashboard?.id === dashboardId,
        ) ?? [];
      if (dashboards.length !== 1) return null;
      const dashboard = dashboards[0];
      const views =
        this.componentCatalog?.views.filter((view) =>
          sameCapabilityReference(view.capability, dashboard.view),
        ) ?? [];
      if (views.length !== 1 || !this.canComposeView(views[0])) return null;
      if (!target)
        return { node, dashboard, label: dashboard.label, presentation: null };
      const raw = dashboard.presentation;
      if (
        raw != null &&
        (typeof raw !== 'object' ||
          Array.isArray(raw) ||
          raw.version !== 1 ||
          !Array.isArray(raw.tiles) ||
          !Array.isArray(raw.filters) ||
          !Array.isArray(raw.columns))
      )
        return null;
      const presentation = resolveDashboardPresentation(
        dashboard,
        views[0],
        document,
        nodeId,
      );
      const key =
        target.kind === 'tile'
          ? 'tiles'
          : target.kind === 'filter'
            ? 'filters'
            : 'columns';
      const items = presentation[key].filter(
        (item) => ('id' in item ? item.id : item.field) === target.id,
      );
      return items.length === 1
        ? { node, dashboard, label: items[0].label, presentation }
        : null;
    } catch {
      return null;
    }
  }
  private dashboardLabelValidation(value: string): string {
    return !value.trim()
      ? 'Enter a label.'
      : value.trim().length > 120
        ? 'Use a label of up to 120 characters.'
        : '';
  }
  private rejectDashboardRename(error: string): false {
    if (this.dashboardRename) this.dashboardRename.error = error;
    this.emitPendingChanges();
    this.focusPendingChanges();
    return false;
  }
  private prepareDashboardChange(): boolean {
    return this.scope !== 'dashboard' || this.commitDashboardRename();
  }
  private emitPendingChanges(): void {
    this.pendingChangesChange.emit(
      this.hasDashboardRename() ||
        this.componentChangesPending ||
        this.detailChangesPending,
    );
  }
  private focusDashboardRename(
    target: 'dashboard' | 'item',
    select = false,
  ): void {
    if (this.managedDashboardSettings) {
      this.focusDashboardTask(`[data-dashboard-rename="${target}"]`, select);
      return;
    }
    setTimeout(() => {
      if (this.ownerViews.focusSuspended()) return;
      const input = this.element.nativeElement.querySelector<HTMLInputElement>(
        `[data-dashboard-rename="${target}"]`,
      );
      input?.focus({ preventScroll: true });
      if (select) input?.select();
    });
  }
  get dashboardControlsDisabled(): boolean {
    return (
      this.disabled ||
      this.loading ||
      this.queueChangesPending ||
      this.dashboardMode !== 'edit'
    );
  }
  get receivingQueue(): string | null {
    const policies =
      this.document?.assignmentPolicies?.filter(
        (p) => p?.scopeCode === 'step' && p.nodeId === this.node?.id,
      ) ?? [];
    return policies.length === 1
      ? (policies[0]?.initialQueueCode ?? null)
      : null;
  }
  get standardStepProgress(): boolean {
    if (this.reviewWorkView) return false;
    return ['1.12', '1.13', '1.14', '1.15', '1.16', '1.17', '1.18', '1.19', '1.20', '1.21', '1.22'].includes(
      this.document?.payloadSchemaVersion ?? '',
    );
  }
  get audienceMode(): 'all' | 'roles' {
    return this.dashboard?.audienceRoleCodes == null ? 'all' : 'roles';
  }
  get visibleAudienceRoles(): string[] {
    return [
      ...new Set([
        ...this.roleCodes,
        ...(this.dashboard?.audienceRoleCodes ?? []),
      ]),
    ];
  }
  roleLabel(code: string): string {
    return code.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
  get dashboardQueueSummary(): string {
    return this.dashboard?.queueScope === 'receiving-queue'
      ? this.receivingQueue
        ? this.roleLabel(this.receivingQueue)
        : 'Choose receiving queue'
      : 'All step work';
  }
  get dashboardAudienceSummary(): string {
    const roles = this.dashboard?.audienceRoleCodes;
    return roles == null
      ? 'Everyone with record access'
      : roles.length
        ? roles.map((role) => this.roleLabel(role)).join(', ')
        : 'Choose roles';
  }
  get includedStatusLabel(): string {
    if (this.reviewWorkView)
      return (
        this.reviewWorkStatuses
          .map(
            (code) =>
              this.statuses.find((item) => item.code === code)?.label ||
              `${code} (saved)`,
          )
          .join(', ') || 'Choose review states'
      );
    const status = String(this.dashboard?.defaults['status'] || '');
    return status
      ? (this.statuses.find((item) => item.code === status)?.label ??
          `${status} (saved)`)
      : 'All statuses';
  }
  get dashboardScopeSummary(): string {
    return `${this.dashboardQueueSummary} · ${this.includedStatusLabel} · ${this.dashboardAudienceSummary}`;
  }
  get selectedKindLabel(): string {
    return this.selection?.kind === 'tile'
      ? 'Summary tile'
      : this.selection?.kind === 'filter'
        ? 'Staff filter'
        : 'Table column';
  }
  get inspectorName(): string {
    return this.queueSettingsOpen
      ? 'Dashboard settings'
      : this.selectedItem
        ? this.selectedItem.label ||
          `Untitled ${this.selectedKindLabel.toLowerCase()}`
        : 'Dashboard layout';
  }
  get queueSettingsIssues(): string[] {
    const dashboard = this.dashboard;
    if (!dashboard) return [];
    const issues: string[] = [];
    if (
      this.hearingDayView &&
      !String(dashboard.defaults['timeZoneId'] || '').trim()
    )
      issues.push(
        'Choose an explicit time zone for this hearing-day dashboard.',
      );
    if (
      this.reviewWorkView &&
      (!this.reviewWorkStatuses.length ||
        this.reviewWorkStatuses.length > 2 ||
        new Set(this.reviewWorkStatuses).size !==
          this.reviewWorkStatuses.length ||
        this.reviewWorkStatuses.some(
          (code) => !this.statuses.some((choice) => choice.code === code),
        ))
    )
      issues.push('Choose one or both supported review states.');
    if (dashboard.queueScope === 'receiving-queue' && !this.receivingQueue)
      issues.push(
        'Remove the saved team queue restriction or configure role assignment for this step.',
      );
    for (const scope of ['workflow', 'step'] as const) {
      const codes = this.includedStatusCodes(scope);
      const choices =
        scope === 'workflow' ? this.workflowStatuses : this.stepStatuses;
      if (
        codes != null &&
        (!codes.length ||
          codes.some((code) => !choices.some((choice) => choice.code === code)))
      )
        issues.push(
          scope === 'workflow'
            ? 'Some saved workflow statuses are unavailable. Select current statuses or show all workflow statuses.'
            : 'Some saved step labels are unavailable. Select current labels or show all step labels.',
        );
    }
    if (dashboard.audienceRoleCodes != null) {
      if (!dashboard.audienceRoleCodes.length)
        issues.push('Choose at least one dashboard role.');
      if (
        this.roleCatalogAvailable &&
        dashboard.audienceRoleCodes.some(
          (role) => !this.roleCodes.includes(role),
        )
      )
        issues.push('Replace or remove unavailable dashboard roles.');
    }
    return issues;
  }
  setQueueScope(value: string): void {
    if (!this.prepareDashboardChange()) return;
    if (value === '' || value === 'receiving-queue')
      this.setDashboardAccess({
        queueScope: value === 'receiving-queue' ? value : null,
      });
  }
  setAudienceMode(value: string): void {
    if (!this.prepareDashboardChange()) return;
    if (value === 'all') this.setDashboardAccess({ audienceRoleCodes: null });
    else if (value === 'roles' && this.audienceMode !== 'roles')
      this.setDashboardAccess({ audienceRoleCodes: [] });
  }
  toggleAudienceRole(code: string, checked: boolean): void {
    if (!this.prepareDashboardChange()) return;
    if (
      checked &&
      (!this.roleCatalogAvailable || !this.roleCodes.includes(code))
    )
      return;
    const roles = new Set(this.dashboard?.audienceRoleCodes ?? []);
    if (checked) roles.add(code);
    else roles.delete(code);
    this.setDashboardAccess({ audienceRoleCodes: [...roles] });
  }
  openQueueSettings(): boolean {
    if (!this.prepareDashboardChange()) return false;
    if (this.beforeSelectionChange && !this.beforeSelectionChange())
      return false;
    this.queueSettingsOpen = true;
    this.responsibilitySettingsOpen = false;
    this.selection = null;
    this.dashboardMode = 'edit';
    this.inspectorOpen = true;
    this.mobilePanel = 'settings';
    this.focusDashboardSettings();
    return true;
  }
  openResponsibilitySettings(): boolean {
    if (!this.prepareDashboardChange()) return false;
    if (
      !this.responsibilitySettingsOpen &&
      this.beforeSelectionChange &&
      !this.beforeSelectionChange()
    )
      return false;
    this.queueSettingsOpen = true;
    this.responsibilitySettingsOpen = true;
    this.selection = null;
    this.dashboardMode = 'edit';
    this.inspectorOpen = true;
    this.mobilePanel = 'settings';
    this.focusDashboardSettings();
    setTimeout(() =>
      this.dashboardInspector?.nativeElement
        .querySelector<HTMLElement>(
          '[data-testid="dashboard-assignment-roles"] h5, [data-testid="convert-role-assignment"]',
        )
        ?.focus(),
    );
    return true;
  }
  closeQueueSettings(): void {
    if (!this.prepareDashboardChange()) return;
    if (this.beforeSelectionChange && !this.beforeSelectionChange()) return;
    this.queueSettingsOpen = false;
    this.responsibilitySettingsOpen = false;
  }
  closeInspector(): void {
    if (!this.prepareDashboardChange()) return;
    if (this.beforeSelectionChange && !this.beforeSelectionChange()) return;
    this.inspectorOpen = false;
    this.mobilePanel = 'page';
    setTimeout(() => {
      const toggle = this.dashboardInspectorToggle?.nativeElement;
      const target = toggle?.offsetParent
        ? toggle
        : this.dashboardSettingsButton?.nativeElement;
      target?.focus({ preventScroll: true });
    });
  }
  closeAddTools(): void {
    if (!this.prepareDashboardChange()) return;
    this.addToolsOpen = false;
    setTimeout(() =>
      this.addElementButton?.nativeElement.focus({ preventScroll: true }),
    );
  }
  toggleInspector(): void {
    if (!this.prepareDashboardChange()) return;
    if (this.dashboardMode !== 'edit') return;
    if (this.inspectorOpen) this.closeInspector();
    else {
      if (this.beforeSelectionChange && !this.beforeSelectionChange()) return;
      this.inspectorOpen = true;
      this.mobilePanel = 'settings';
      this.focusDashboardSettings();
    }
  }
  setCanvasSize(value: string): void {
    if (!this.prepareDashboardChange()) return;
    if (value === 'available' || value === 'phone') this.canvasSize = value;
  }
  setCanvasSizeFromControl(value: string, control: HTMLSelectElement): void {
    this.setCanvasSize(value);
    control.value = this.canvasSize;
  }
  setMobilePanel(panel: 'page' | 'settings'): void {
    if (!this.prepareDashboardChange()) return;
    if (
      panel === 'page' &&
      this.beforeSelectionChange &&
      !this.beforeSelectionChange()
    )
      return;
    this.mobilePanel = panel;
    if (panel === 'settings') {
      this.inspectorOpen = true;
      this.focusDashboardSettings();
    }
  }
  private focusDashboardSettings(): void {
    setTimeout(() => {
      if (this.ownerViews.focusSuspended()) return;
      if (this.dashboardMode !== 'edit' || this.mobilePanel !== 'settings')
        return;
      const panel = this.dashboardInspector?.nativeElement;
      if (!panel) return;
      panel.scrollTop = 0;
      const heading = panel.querySelector<HTMLElement>(
        '[data-dashboard-heading]',
      );
      const panels = this.dashboardPanels?.nativeElement;
      if (panels?.offsetParent)
        panels.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
      heading?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
      heading?.focus({ preventScroll: true });
    });
  }
  setDashboardMode(
    mode: 'edit' | 'try',
    origin?: WorkflowDashboardSettingsOrigin,
  ): void {
    const entered = this.managedDashboardSettings
      ? this.settingsTarget(this.settingsId())
      : null;
    const reference = JSON.stringify(entered?.dashboard.view);
    if (
      this.managedDashboardSettings &&
      (!this.settingsCurrent(origin) ||
        this.dashboardTask !== 'canvas' ||
        !entered)
    )
      return;
    if (!this.prepareDashboardChange()) return;
    if (this.beforeSelectionChange && !this.beforeSelectionChange()) return;
    if (this.managedDashboardSettings) {
      if (!origin) return;
      const current = this.settingsTarget(origin.dashboardId);
      if (
        !current ||
        origin.dashboardId !== this.settingsId() ||
        origin.mode !== this.dashboardMode ||
        reference !== JSON.stringify(current.dashboard.view) ||
        origin.owner !== this.removalOwnerKey() ||
        origin.context !== this.removalContext() ||
        origin.view !== this.dashboardSettingsView?.() ||
        (mode === 'try' && this.settingsSource(current.dashboard).reason)
      ) {
        this.error =
          'This exact dashboard or its owner changed. Review its current source before opening Preview.';
        return;
      }
      if (
        mode === 'try' &&
        current.dashboard.view.key.code === 'workflow.preparation.work' &&
        !this.dashboardPreviewState.available
      ) {
        this.error = this.dashboardPreviewState.reason;
        return;
      }
    }
    this.dashboardMode = mode;
    if (mode === 'try') {
      this.mobilePanel = 'page';
      this.addToolsOpen = false;
    }
  }
  requestDashboardMode(
    mode: string,
    origin?: WorkflowDashboardSettingsOrigin,
  ): void {
    if (mode === 'edit' || mode === 'try') this.setDashboardMode(mode, origin);
  }
  requestMobilePanel(panel: string): void {
    if (panel === 'page' || panel === 'settings') this.setMobilePanel(panel);
  }
  get modeTabs() {
    return [
      { id: 'edit', label: 'Design', disabled: !this.supportedDashboard },
      { id: 'try', label: 'Preview', disabled: !this.supportedDashboard },
    ];
  }
  readonly mobileTabs = [
    { id: 'page', label: 'Page' },
    { id: 'settings', label: 'Settings' },
  ];
  get editableRouting() {
    return !!this.document && hasEditableRouting(this.document);
  }
  get canEnableRouting() {
    return ['1.0', '1.1', '1.2'].includes(
      this.document?.payloadSchemaVersion ?? '',
    );
  }
  enableRouting() {
    if (!this.disabled && this.document)
      this.documentChange.emit(enableEditableRouting(this.document));
  }
  applyPendingChanges(latestDocument?: WorkflowDefinitionDocument): boolean {
    const hadRename = this.hasDashboardRename();
    if (!this.commitDashboardRename(latestDocument)) return false;
    let latest = hadRename ? this.document : latestDocument;
    const detail = this.detailComposer;
    if (detail?.hasPendingChanges()) {
      const previousDisabled = detail.disabled;
      detail.disabled = this.disabled || this.loading;
      try {
        if (!detail.applyPendingChanges(latest ?? undefined)) return false;
      } finally {
        detail.disabled = previousDisabled;
      }
      latest = detail.document;
      this.document = latest;
      this.node = detail.node;
    }
    const editor = this.componentsEditor;
    if (!editor?.hasPendingChanges()) return true;
    if (latest && this.node) {
      this.document = latest;
      this.node =
        latest.nodes.find((node) => node.id === this.node!.id) ?? this.node;
      editor.document = latest;
      editor.node = this.node;
    }
    const previousDisabled = editor.disabled;
    editor.disabled = this.disabled;
    try {
      return editor.applyPendingChanges();
    } finally {
      editor.disabled = previousDisabled;
    }
  }
  discardRouteEdits(): void {
    if (this.scope === 'behavior') {
      this.componentsEditor?.discardRouteChanges();
      this.componentChangesPending =
        this.componentsEditor?.hasPendingChanges() === true;
      this.emitPendingChanges();
      return;
    }
    this.resetPendingChanges();
  }
  /** Whole-editor reset for explicitly named multi-editor or whole-draft discard. */
  resetPendingChanges(): void {
    this.dashboardRename = null;
    this.detailComposer?.discardPendingChanges();
    this.componentsEditor?.resetPendingChanges();
    this.componentChangesPending = false;
    this.detailChangesPending = false;
    this.emitPendingChanges();
  }
  focusPendingChanges(): void {
    if (this.detailComposer?.hasPendingChanges()) {
      this.detailComposer.focusPendingChanges();
      return;
    }
    if (this.dashboardRename) {
      this.focusDashboardRename(
        this.dashboardRename.target ? 'item' : 'dashboard',
      );
      return;
    }
    this.componentsEditor?.focusPendingChanges();
  }
  showWorkflowStatuses(): void {
    this.componentsEditor?.showWorkflowStatuses();
  }
  onWorkflowStatusesOwnerRequested(request: WorkflowStatusOwnerRequest): void {
    const document = this.latestDocument ? this.latestDocument() : this.document;
    const view = this.workflowStatusesView?.();
    if (!this.externalWorkflowStatuses || !this.active || this.scope !== 'behavior' || !this.ownerContext || !view || !document
      || request.origin !== 'routing' || request.nodeId !== this.node?.id || request.view !== view
      || request.owner !== JSON.stringify(this.ownerContext()) || request.source !== JSON.stringify(document)
      || document.nodes.filter(node => node?.id === request.nodeId && node.typeCode === 'human_step').length !== 1) return;
    this.workflowStatusesOwnerRequested.emit(request);
  }
  selectBehaviorAction(command: StepCommand, outcomes = false): void {
    if (outcomes) this.componentsEditor?.selectOutcomes(command);
    else this.componentsEditor?.selectAction(command);
  }
  private loadedNode = '';
  private catalogContext: string | null = null;
  get reviewWorkView(): boolean {
    return (
      this.installedView?.capability.key.provider === 'platform' &&
      this.installedView.capability.key.code === 'workflow.review.work'
    );
  }
  get reviewView(): boolean {
    return ['workflow.review.records', 'workflow.review.work'].includes(
      this.installedView?.capability.key.code || '',
    );
  }
  get reviewWorkStatuses(): string[] {
    return reviewWorkDeclaredStatuses(this.dashboard?.defaults['statuses']);
  }
  toggleReviewWorkStatus(code: string, checked: boolean): void {
    if (!this.prepareDashboardChange()) return;
    if (
      !this.reviewWorkView ||
      this.dashboardControlsDisabled ||
      !this.statuses.some((choice) => choice.code === code)
    )
      return;
    const selected = new Set(this.reviewWorkStatuses);
    if (checked) selected.add(code);
    else selected.delete(code);
    const statuses = this.statuses
      .filter((choice) => selected.has(choice.code))
      .map((choice) => choice.code);
    const presentation = this.presentation
      ? structuredClone(this.presentation)
      : null;
    if (presentation) {
      presentation.tiles = presentation.tiles.filter(
        (tile) => !tile.status || statuses.includes(tile.status),
      );
      presentation.filters = presentation.filters.map((filter) => {
        if (filter.field !== 'status') return filter;
        const retained = filter.allowedValues?.filter((value) =>
          statuses.includes(value),
        );
        const allowedValues = retained?.length ? retained : null;
        return {
          ...filter,
          ...(filter.allowedValues != null ? { allowedValues } : {}),
          initialValue:
            statuses.includes(filter.initialValue) &&
            (allowedValues == null ||
              allowedValues.includes(filter.initialValue))
              ? filter.initialValue
              : '',
        };
      });
    }
    this.setDashboard({
      ...(presentation
        ? updateDashboardPresentation(
            this.dashboard!,
            presentation,
            this.installedView,
          )
        : {}),
      defaults: {
        ...this.dashboard!.defaults,
        statuses: statuses.map((statusCode) => ({ statusCode })),
      },
    });
  }
  get hearingDayView(): boolean {
    return (
      this.installedView?.capability.key.provider === 'hearings' &&
      this.installedView.capability.key.code === 'hearings.day-work'
    );
  }
  setHearingDayDefault(field: 'timeZoneId' | 'day', value: string): void {
    if (!this.prepareDashboardChange()) return;
    if (
      !this.hearingDayView ||
      !this.dashboard ||
      this.dashboardControlsDisabled
    )
      return;
    const defaults = { ...this.dashboard.defaults };
    if (field === 'day' && !value) delete defaults[field];
    else defaults[field] = value;
    this.setDashboard({ defaults });
  }
  get installedView() {
    try {
      return this.componentCatalog?.views.find(
        (view) =>
          !!this.dashboard &&
          sameCapabilityReference(view.capability, this.dashboard.view),
      );
    } catch {
      return undefined;
    }
  }
  @ViewChild(WorkflowStepComponentsEditorComponent)
  private componentsEditor?: WorkflowStepComponentsEditorComponent;
  @ViewChild(WorkflowDetailComposerComponent)
  private detailComposer?: WorkflowDetailComposerComponent;
  private noticeRequirementEmission: WorkflowNoticeRequirementRequest | null =
    null;

  onNoticeRequirementRequested(request: WorkflowNoticeRequirementRequest): void {
    const current = this.latestDocument ? this.latestDocument() : this.document;
    if (
      !request ||
      !current ||
      this.scope !== 'detail' ||
      !this.active ||
      this.loading ||
      !this.detailComposer?.isCurrentNoticeRequirementRequest(request, current)
    )
      return;
    this.noticeRequirementEmission = request;
    try {
      this.noticeRequirementRequested.emit(request);
    } finally {
      this.noticeRequirementEmission = null;
    }
  }

  isCurrentNoticeRequirementRequest(
    request: WorkflowNoticeRequirementRequest,
    current: WorkflowDefinitionDocument | null = this.latestDocument
      ? this.latestDocument()
      : this.document,
  ): boolean {
    return (
      !!request &&
      !!current &&
      this.noticeRequirementEmission === request &&
      this.scope === 'detail' &&
      this.active &&
      !this.loading &&
      this.detailComposer?.isCurrentNoticeRequirementRequest(request, current) ===
        true
    );
  }

  isNoticeRequirementTargetCurrent(
    request: WorkflowNoticeRequirementRequest,
    current: WorkflowDefinitionDocument | null = this.latestDocument
      ? this.latestDocument()
      : this.document,
  ): boolean {
    return (
      !!request &&
      !!current &&
      this.scope === 'detail' &&
      this.active &&
      !this.loading &&
      this.detailComposer?.isNoticeRequirementTargetCurrent(request, current) ===
        true
    );
  }
  dashboardMode: 'edit' | 'try' = 'edit';
  selection: DashboardSelection | null = null;
  showSources = false;
  additionalSource = '';
  private loadedResetVersion = -1;
  readonly viewIdentity = (view: StepAuthoringView) =>
    capabilityIdentity(view.capability.key);
  readonly tones = ['neutral', 'info', 'success', 'warning', 'danger'] as const;
  readonly filterKinds = [
    { field: 'search', label: 'Reference' },
    { field: 'status', label: 'Progress' },
    { field: 'history', label: 'Work' },
    { field: 'workflowStatus', label: 'Workflow status' },
    { field: 'stepStatus', label: 'Step label' },
    { field: 'stepProgress', label: 'Step progress' },
    { field: 'assignment', label: 'Assignment' },
  ] as const;
  private cachedPresentation: {
    document: WorkflowDefinitionDocument | null;
    dashboard: NonNullable<WorkflowStepDeclaration['dashboards']>[number];
    view: StepAuthoringView;
    value: DashboardPresentation;
  } | null = null;
  private cachedColumns: {
    view: StepAuthoringView;
    statusContext: string;
    value: ReturnType<typeof dashboardColumns>;
  } | null = null;
  private readonly noColumns: ReturnType<typeof dashboardColumns> = [];
  readonly historyChoices = [
    { code: 'current', label: 'Current work' },
    { code: 'history', label: 'Include past visits' },
  ];
  private readonly allStatusesChoice = { code: '', label: 'All included work' };
  readonly trackId = (_: number, item: { id: string }) => item.id;
  readonly trackField = (_: number, item: { field: string }) => item.field;
  readonly trackCode = (_: number, item: { code: string }) => item.code;
  readonly trackView = (_: number, item: StepAuthoringView) =>
    this.viewIdentity(item);
  get presentation(): DashboardPresentation | null {
    const dashboard = this.dashboard,
      view = this.installedView;
    if (!dashboard || !view) return null;
    if (
      this.cachedPresentation?.dashboard !== dashboard ||
      this.cachedPresentation.view !== view ||
      this.cachedPresentation.document !== this.document
    ) {
      this.cachedPresentation = {
        document: this.document,
        dashboard,
        view,
        value: resolveDashboardPresentation(
          dashboard,
          view,
          this.document,
          this.node?.id,
        ),
      };
    }
    return this.cachedPresentation.value;
  }
  get columns() {
    const view = this.installedView;
    if (!view) return this.noColumns;
    const statusContext = JSON.stringify([
      this.document?.payloadSchemaVersion,
      this.document?.statusConfiguration?.statuses,
      this.node?.id,
      this.document?.nodes.find((node) => node.id === this.node?.id)
        ?.statusConfiguration?.statuses,
      this.document?.assignmentPolicies?.filter(
        (policy) =>
          policy?.scopeCode === 'step' && policy.nodeId === this.node?.id,
      ),
    ]);
    if (
      this.cachedColumns?.view !== view ||
      this.cachedColumns.statusContext !== statusContext
    )
      this.cachedColumns = {
        view,
        statusContext,
        value: dashboardColumns(view, this.document, this.node?.id),
      };
    return this.cachedColumns.value;
  }
  get availableColumns() {
    return this.columns.filter(
      (column) =>
        !this.presentation?.columns.some((item) => item.field === column.field),
    );
  }
  get assignmentSupported(): boolean {
    return !this.reviewWorkView && hasDashboardAssignment(this.document);
  }
  get hasAssignmentColumn(): boolean {
    return !!this.presentation?.columns.some(
      (column) => column.field === 'assigneeDisplayName',
    );
  }
  get assignmentSetupSummary(): string {
    const policies =
      this.document?.assignmentPolicies?.filter(
        (policy) =>
          policy?.scopeCode === 'step' && policy.nodeId === this.node?.id,
      ) ?? [];
    const policy = policies.length === 1 ? policies[0] : null;
    if (!policy || this.declaration?.behavior.assignmentPolicyId !== policy.id)
      return 'Choose who can receive this step’s work.';
    if (policy.assignmentBasisCode !== 'roles')
      return 'Earlier queue assignment is saved. Set up assignment by role.';
    const roles = (policy.eligibleRoleCodes ?? [])
      .filter((code): code is string => !!code)
      .map((code) => this.roleLabel(code));
    if (!roles.length) return 'Choose who can receive this step’s work.';
    const mode =
      policy.assignmentModeCode === 'claim'
        ? 'Staff claim work themselves'
        : policy.assignmentModeCode === 'direct'
          ? 'Assign from dashboard'
          : 'Assign from dashboard or let staff claim work';
    return `${roles.join(', ')} · ${mode}`;
  }
  readonly assignmentChoices = dashboardAssignmentChoices;
  get availableFilters() {
    return this.filterKinds
      .filter(
        (filter) =>
          (filter.field !== 'history' ||
            (!this.hearingDayView && !this.reviewWorkView)) &&
          (filter.field !== 'status' ||
            (this.installedView?.filterFields.includes('status') &&
              (this.reviewView ||
                !this.presentation?.filters.some(
                  (item) => item.field === 'stepProgress',
                )))) &&
          (!this.reviewWorkView ||
            ['search', 'status'].includes(filter.field)) &&
          (filter.field !== 'assignment' || this.assignmentSupported) &&
          (filter.field !== 'workflowStatus' ||
            this.workflowStatuses.length > 0) &&
          (filter.field !== 'stepStatus' || this.stepStatuses.length > 0) &&
          (filter.field !== 'stepProgress' ||
            (this.standardStepProgress &&
              (this.reviewView ||
                !this.presentation?.filters.some(
                  (item) => item.field === 'status',
                )))) &&
          !this.presentation?.filters.some(
            (item) => item.field === filter.field,
          ),
      )
      .map((filter) =>
        filter.field === 'status'
          ? {
              ...filter,
              label: this.reviewView ? 'Review result' : 'Step progress',
            }
          : filter,
      );
  }
  get statuses() {
    if (this.reviewWorkView)
      return this.installedView?.queueStatusChoices ?? [];
    if (
      this.installedView?.capability.key.code === 'workflow.preparation.work' &&
      this.standardStepProgress
    )
      return this.stepProgressChoices;
    return this.installedView?.statusChoices ?? [];
  }
  get queueStatuses() {
    if (this.reviewWorkView)
      return this.statuses.filter((choice) =>
        this.reviewWorkStatuses.includes(choice.code),
      );
    if (
      this.installedView?.capability.key.code === 'workflow.preparation.work' &&
      this.standardStepProgress
    )
      return this.stepProgressChoices;
    return this.installedView?.queueStatusChoices ?? this.statuses;
  }
  get workflowStatuses() {
    if (this.reviewWorkView) return [];
    return dashboardBusinessStatusChoices(
      this.document,
      this.node?.id,
      'workflow',
    );
  }
  get stepStatuses() {
    if (this.reviewWorkView) return [];
    return dashboardBusinessStatusChoices(this.document, this.node?.id, 'step');
  }
  get stepProgressChoices() {
    return dashboardStepProgressChoices.filter(
      (choice) =>
        choice.code !== 'bypassed' ||
        this.node?.stepProgressConfiguration?.bypass,
    );
  }
  get priorityStatuses() {
    return (this.presentation?.workflowStatusPriorityCodes ?? []).map(
      (code) => ({
        code,
        label:
          this.workflowStatuses.find((status) => status.code === code)?.label ||
          `${code} (unavailable)`,
      }),
    );
  }
  get availablePriorityStatuses() {
    return this.workflowStatuses.filter(
      (status) =>
        !this.presentation?.workflowStatusPriorityCodes?.includes(
          status.code,
        ) &&
        (this.dashboard?.workflowStatusCodes == null ||
          this.dashboard.workflowStatusCodes.includes(status.code)),
    );
  }
  addPriorityStatus(code: string): void {
    if (!this.prepareDashboardChange()) return;
    if (!this.availablePriorityStatuses.some((status) => status.code === code))
      return;
    this.editPresentation((presentation) => ({
      ...presentation,
      workflowStatusPriorityCodes: [
        ...(presentation.workflowStatusPriorityCodes ?? []),
        code,
      ],
    }));
  }
  movePriorityStatus(index: number, direction: number): void {
    if (!this.prepareDashboardChange()) return;
    this.editPresentation((presentation) => {
      const codes = [...(presentation.workflowStatusPriorityCodes ?? [])];
      const destination = index + direction;
      if (index < 0 || destination < 0 || destination >= codes.length)
        return presentation;
      [codes[index], codes[destination]] = [codes[destination], codes[index]];
      return { ...presentation, workflowStatusPriorityCodes: codes };
    });
  }
  removePriorityStatus(code: string): void {
    if (!this.prepareDashboardChange()) return;
    this.editPresentation((presentation) => {
      const codes = presentation.workflowStatusPriorityCodes?.filter(
        (value) => value !== code,
      );
      const rest = { ...presentation };
      delete rest.workflowStatusPriorityCodes;
      return codes?.length
        ? { ...rest, workflowStatusPriorityCodes: codes }
        : rest;
    });
  }
  get tileStatuses() {
    if (this.reviewWorkView) return this.queueStatuses;
    if (this.selectedTile?.statusScope === 'stepProgress')
      return this.stepProgressChoices;
    return dashboardTileStatusChoices(
      this.installedView,
      this.document,
      this.node?.id,
      this.selectedTile?.statusScope ?? 'progress',
    );
  }
  get unrecognizedTileStatus(): string {
    const code = this.selectedTile?.status;
    return code && !this.tileStatuses.some((status) => status.code === code)
      ? code
      : '';
  }
  includedStatusCodes(scope: 'workflow' | 'step'): string[] | null {
    return (
      (scope === 'workflow'
        ? this.dashboard?.workflowStatusCodes
        : this.dashboard?.stepStatusCodes) ?? null
    );
  }
  setIncludedStatuses(
    scope: 'workflow' | 'step',
    codes: string[] | null,
  ): void {
    if (!this.prepareDashboardChange()) return;
    const choices =
      scope === 'workflow' ? this.workflowStatuses : this.stepStatuses;
    if (codes?.some((code) => !choices.some((choice) => choice.code === code)))
      return;
    const included = codes?.length ? codes : null;
    const presentation = this.presentation
      ? structuredClone(this.presentation)
      : null;
    const field = scope === 'workflow' ? 'workflowStatus' : 'stepStatus';
    const available = choices
      .filter((choice) => included == null || included.includes(choice.code))
      .map((choice) => choice.code);
    if (presentation) {
      if (
        scope === 'workflow' &&
        presentation.workflowStatusPriorityCodes != null
      ) {
        const priority = presentation.workflowStatusPriorityCodes.filter(
          (code) => available.includes(code),
        );
        if (priority.length)
          presentation.workflowStatusPriorityCodes = priority;
        else delete presentation.workflowStatusPriorityCodes;
      }
      presentation.filters = presentation.filters.map((filter) => {
        if (filter.field !== field) return filter;
        const retained = filter.allowedValues?.filter((code) =>
          available.includes(code),
        );
        const allowedValues = retained?.length ? retained : null;
        const startingValueAllowed =
          available.includes(filter.initialValue) &&
          (allowedValues == null ||
            allowedValues.includes(filter.initialValue));
        return {
          ...filter,
          ...(filter.allowedValues != null ? { allowedValues } : {}),
          initialValue: startingValueAllowed ? filter.initialValue : '',
        };
      });
    }
    this.setDashboard({
      ...(presentation
        ? updateDashboardPresentation(
            this.dashboard!,
            presentation,
            this.installedView,
          )
        : {}),
      ...(scope === 'workflow'
        ? { workflowStatusCodes: included }
        : { stepStatusCodes: included }),
    });
  }
  toggleIncludedStatus(
    scope: 'workflow' | 'step',
    code: string,
    checked: boolean,
  ): void {
    if (!this.prepareDashboardChange()) return;
    const codes = new Set(this.includedStatusCodes(scope) ?? []);
    if (checked) codes.add(code);
    else codes.delete(code);
    this.setIncludedStatuses(scope, [...codes]);
  }
  setTileScope(scope: string): void {
    if (!this.prepareDashboardChange()) return;
    if (this.reviewWorkView && scope !== 'progress') return;
    if (
      scope === 'progress' ||
      scope === 'stepProgress' ||
      (scope === 'assignment' && this.assignmentSupported) ||
      (scope === 'workflow' && this.workflowStatuses.length) ||
      (scope === 'step' && this.stepStatuses.length)
    )
      this.setTile({ statusScope: scope, status: null });
  }
  addTileChoice(value: string): void {
    if (!this.prepareDashboardChange()) return;
    const separator = value.indexOf(':');
    if (separator === -1) {
      this.addTile(value === '*' ? '' : value);
      return;
    }
    const scope = value.slice(0, separator);
    if (
      scope === 'workflow' ||
      scope === 'step' ||
      scope === 'stepProgress' ||
      (scope === 'assignment' && this.assignmentSupported)
    )
      this.addTile(value.slice(separator + 1), scope);
  }
  get supportedViews() {
    return (
      this.componentCatalog?.views.filter((view) =>
        this.canComposeView(view),
      ) ?? []
    );
  }
  get defaultView() {
    return this.supportedViews.find(
      (view) => view.capability.key.code === 'workflow.preparation.work',
    );
  }
  private canComposeView(view: StepAuthoringView): boolean {
    const key = view.capability.key;
    return (
      key.kind === 'view' &&
      key.version === 1 &&
      ((key.provider === 'platform' &&
        [
          'workflow.preparation.work',
          'workflow.review.records',
          'workflow.review.work',
        ].includes(key.code)) ||
        (key.provider === 'hearings' && key.code === 'hearings.day-work'))
    );
  }
  get selectedTile() {
    return this.selection?.kind === 'tile'
      ? this.presentation?.tiles.find((item) => item.id === this.selection!.id)
      : undefined;
  }
  get selectedFilter() {
    return this.selection?.kind === 'filter'
      ? this.presentation?.filters.find(
          (item) => item.field === this.selection!.id,
        )
      : undefined;
  }
  get selectedColumn() {
    return this.selection?.kind === 'column'
      ? this.presentation?.columns.find(
          (item) => item.field === this.selection!.id,
        )
      : undefined;
  }
  get selectedItem() {
    return this.selectedTile ?? this.selectedFilter ?? this.selectedColumn;
  }
  get selectedIndex() {
    if (!this.presentation || !this.selection) return -1;
    return this.selection.kind === 'tile'
      ? this.presentation.tiles.findIndex(
          (item) => item.id === this.selection!.id,
        )
      : this.selection.kind === 'filter'
        ? this.presentation.filters.findIndex(
            (item) => item.field === this.selection!.id,
          )
        : this.presentation.columns.findIndex(
            (item) => item.field === this.selection!.id,
          );
  }
  get selectedCount() {
    if (!this.presentation || !this.selection) return 0;
    return this.selection.kind === 'tile'
      ? this.presentation.tiles.length
      : this.selection.kind === 'filter'
        ? this.presentation.filters.length
        : this.presentation.columns.length;
  }
  get requiredColumn() {
    return (
      this.selection?.kind === 'column' &&
      this.columns.some(
        (column) => column.field === this.selection!.id && column.required,
      )
    );
  }
  get filterChoices() {
    const field = this.selectedFilter?.field;
    if (field === 'history') return this.historyChoices;
    if (!field) return this.queueStatuses;
    const choices = dashboardFilterChoices(
      this.installedView,
      this.document,
      this.node?.id,
      field,
    );
    if (field === 'stepProgress') return this.stepProgressChoices;
    const included =
      field === 'workflowStatus'
        ? this.dashboard?.workflowStatusCodes
        : field === 'stepStatus'
          ? this.dashboard?.stepStatusCodes
          : null;
    if (field === 'status' && this.reviewWorkView)
      return choices.filter((choice) =>
        this.reviewWorkStatuses.includes(choice.code),
      );
    return included == null
      ? choices
      : choices.filter((choice) => included.includes(choice.code));
  }
  get initialFilterChoices() {
    const allowed = this.selectedFilter?.allowedValues;
    const choices =
      allowed == null
        ? this.filterChoices
        : this.filterChoices.filter((choice) => allowed.includes(choice.code));
    const field = this.selectedFilter?.field;
    const scope =
      field === 'status' ? this.dashboard?.defaults['status'] : null;
    const included =
      field === 'workflowStatus'
        ? this.dashboard?.workflowStatusCodes
        : field === 'stepStatus'
          ? this.dashboard?.stepStatusCodes
          : null;
    return field === 'status' ||
      field === 'workflowStatus' ||
      field === 'stepStatus' ||
      field === 'stepProgress' ||
      field === 'assignment'
      ? [
          this.allStatusesChoice,
          ...choices.filter(
            (choice) =>
              (!scope || choice.code === scope) &&
              (included == null || included.includes(choice.code)),
          ),
        ]
      : choices;
  }
  get filterAllowedValues() {
    const available = this.filterChoices.map((choice) => choice.code);
    return (
      this.selectedFilter?.allowedValues?.filter((code) =>
        available.includes(code),
      ) ?? available
    );
  }
  get unrecognizedStatus() {
    const status = String(this.dashboard?.defaults['status'] || '');
    return status && !this.statuses.some((item) => item.code === status)
      ? status
      : '';
  }
  selectedDashboard = '';
  error = '';
  loading = false;
  private readonly dialog = inject(MatDialog);
  private previewRef?: MatDialogRef<unknown>;
  @ViewChild('previewTemplate') private previewTemplate!: TemplateRef<unknown>;
  openPreview(): void {
    if (!this.prepareDashboardChange()) return;
    if (this.scope === 'detail' && !this.applyPendingChanges()) return;
    if (
      !this.declaration ||
      !this.document ||
      !this.node ||
      this.previewRef ||
      (this.scope === 'dashboard' && !this.supportedDashboard)
    )
      return;
    this.previewRef = this.dialog.open(this.previewTemplate, {
      width: '1100px',
      maxWidth: 'calc(100vw - 16px)',
      maxHeight: '90vh',
      ariaLabel: 'Step page preview',
      autoFocus: 'first-tabbable',
      restoreFocus: true,
    });
    this.previewRef.afterClosed().subscribe(() => {
      this.previewRef = undefined;
    });
  }
  closePreview(): void {
    this.previewRef?.close();
  }
  ngOnDestroy(): void {
    this.settingsDestroyed = true; this.settingsFocusRequest++; this.settingsGeneration++;
    this.elementFocusRequest++; this.elementGeneration++; this.elementPickerOpen = false;
    this.ownerFocusRequest++;
    this.sourceFocusRequest++; this.sourcePickerGeneration++; this.sourcePickerOpen = false;
    this.rememberOwnerView();
    this.closePreview();
  }
  get declaration() {
    return this.node?.declaration;
  }
  get dashboard() {
    const rows = this.declaration?.dashboards;
    if (!Array.isArray(rows)) return undefined;
    return rows.find((d) => d?.id === this.selectedDashboard) ?? rows[0];
  }
  get supportedDashboard() {
    return !!this.installedView && this.canComposeView(this.installedView);
  }
  get commands() {
    return this.declaration?.behavior.commands ?? [];
  }
  applyComponentDocument(document: WorkflowDefinitionDocument) {
    if (this.disabled) return;
    if (this.scope === 'dashboard' && this.hasDashboardRename()) {
      this.commitDashboardRename(document);
      return;
    }
    const previousIds = new Set(
      this.node?.declaration?.dashboards?.map((view) => view.id),
    );
    this.document = document;
    this.node = document.nodes.find((node) => node.id === this.node?.id)!;
    const added = this.node.declaration?.dashboards?.find(
      (view) => !previousIds.has(view.id),
    );
    if (added) {
      this.selectDashboard(added.id);
      const installed = this.installedView;
      if (installed && this.canComposeView(installed)) {
        this.setDashboard({
          ...updateDashboardPresentation(
            added,
            createDashboardPresentation(installed, this.document, this.node.id),
            installed,
          ),
          label: this.node.displayName || this.node.stepCode || installed.label,
        });
        return;
      }
    }
    this.documentChange.emit(document);
  }
  ngOnChanges(): void {
    const elementView = this.dashboardElementView?.() ?? null;
    const elementAuthority = JSON.stringify([this.removalOwnerKey(), this.removalContext()]);
    if (this.active && (this.dashboardElementViewState === undefined || elementView === this.dashboardElementViewState)
      && JSON.stringify(this.document) === JSON.stringify(this.sourceDocument()) && (!this.hasPendingChanges() || this.elementAuthority === elementAuthority)) {
      this.elementAuthority = elementAuthority; this.elementRenderedView = elementView;
    }
    const sourceView = this.dashboardSourceView?.() ?? null;
    const sourceAuthority = JSON.stringify([this.removalOwnerKey(), this.removalContext()]);
    if (this.active && (this.dashboardSourceViewState === undefined || sourceView === this.dashboardSourceViewState)
      && JSON.stringify(this.document) === JSON.stringify(this.sourceDocument()) && (!this.hasPendingChanges() || this.sourceAuthority === sourceAuthority)) {
      this.sourceAuthority = sourceAuthority; this.sourceRenderedView = sourceView;
    }
    const removalView = this.dashboardRemovalView?.() ?? null;
    const removalAuthority = JSON.stringify([this.removalOwnerKey(), this.removalContext()]);
    if (this.active && (this.dashboardRemovalViewState === undefined || removalView === this.dashboardRemovalViewState)
      && JSON.stringify(this.document) === JSON.stringify(this.latestDocument?.() ?? this.document)
      && (!this.hasPendingChanges() || this.removalAuthority === removalAuthority)) {
      this.removalAuthority = removalAuthority;
      this.removalRenderedView = removalView;
    }
    const viewKey = this.document && this.node
      ? this.ownerViews.key(this.document, this.node.id, `presentation:${this.scope}`) : '';
    const ownerChanged = viewKey !== this.ownerViewKey;
    const reactivated = this.active && !this.ownerActive;
    if (ownerChanged || this.active !== this.ownerActive) this.ownerFocusRequest++;
    if (ownerChanged || !this.active) this.rememberOwnerView();
    if (ownerChanged) {
      this.ownerViewKey = viewKey;
      this.ownerFocus = null;
      this.ownerFocusSelection = '';
    }
    this.error = '';
    const documentIdentity = JSON.stringify([
      this.document?.workflowCode,
      this.document?.definitionVersionCode,
      this.scope,
    ]);
    const context = JSON.stringify([
      this.document?.workflowCode,
      this.document?.packReference,
    ]);
    if (this.catalogContext !== null && context !== this.catalogContext) {
      this.componentCatalog = null;
      this.componentCapabilities = [];
    }
    this.catalogContext = context;
    if (
      this.loadedNode !== this.node?.id ||
      this.loadedResetVersion !== this.resetVersion ||
      this.loadedDocumentIdentity !== documentIdentity
    ) {
      const hadPending =
        this.hasDashboardRename() ||
        this.componentChangesPending ||
        this.componentsEditor?.hasPendingChanges() === true ||
        this.detailChangesPending ||
        this.detailComposer?.hasPendingChanges() === true;
      const dashboardId = this.selectedDashboard || this.dashboard?.id || '';
      const keepDashboard =
        this.loadedNode === this.node?.id &&
        this.loadedDocumentIdentity === documentIdentity &&
        this.declaration?.dashboards?.filter(
          (dashboard) => dashboard.id === dashboardId,
        ).length === 1;
      const keepSelection =
        keepDashboard &&
        this.selection &&
        this.document &&
        this.node &&
        this.resolveDashboardRenameTarget(
          this.document,
          this.node.id,
          dashboardId,
          this.selection,
        ) !== null;
      this.selectedDashboard = keepDashboard ? dashboardId : '';
      if (!keepDashboard) { this.dashboardTask = 'canvas'; this.settingsReturn = null; }
      if (!keepDashboard || (this.selection && !keepSelection)) {
        this.selection = null;
        this.queueSettingsOpen = true;
        this.inspectorOpen = true;
        this.responsibilitySettingsOpen = false;
        this.mobilePanel = keepDashboard ? 'settings' : 'page';
      }
      this.dashboardRename = null;
      this.dashboardMode = 'edit';
      this.showSources = false;
      this.addToolsOpen = false;
      this.additionalSource = '';
      this.loadedNode = this.node?.id ?? '';
      this.loadedResetVersion = this.resetVersion;
      this.loadedDocumentIdentity = documentIdentity;
      this.componentsEditor?.resetPendingChanges();
      this.componentChangesPending = false;
      this.detailComposer?.discardPendingChanges();
      this.detailChangesPending = false;
      if (hadPending) this.emitPendingChanges();
    }
    if (
      this.selectedDashboard &&
      !this.declaration?.dashboards?.some(
        (item) => item.id === this.selectedDashboard,
      )
    )
      this.selectDashboard('');
    const restored = ownerChanged && this.scope === 'dashboard' && this.restoreOwnerView();
    // A saved finding enters the same guarded settings task as a native handoff.
    // Reconcile its current owner before that handoff checks the rendered authority.
    const settingsView = this.dashboardSettingsView?.() ?? null;
    const settingsAuthority = JSON.stringify([this.removalOwnerKey(), this.removalContext()]);
    if (this.active && (this.dashboardSettingsViewState === undefined || settingsView === this.dashboardSettingsViewState)
      && JSON.stringify(this.document) === JSON.stringify(this.sourceDocument())) {
      if (settingsAuthority !== this.settingsAuthority || settingsView !== this.settingsRenderedView) this.settingsGeneration++;
      this.settingsAuthority = settingsAuthority; this.settingsRenderedView = settingsView;
    }
    if (this.active && this.focusResponsibility !== this.lastResponsibilityFocus && this.focusResponsibility &&
      !this.ownerViews.hasConsumedFocus(this.ownerViewKey, this.focusResponsibility)) {
      const focus = this.focusResponsibility;
      if (focus?.dashboardId !== undefined) {
        const matches =
          this.declaration?.dashboards?.filter(
            (dashboard) => dashboard.id === focus.dashboardId,
          ) ?? [];
        if (matches.length !== 1) {
          this.lastResponsibilityFocus = focus;
          this.error =
            'The dashboard for this finding is unavailable or ambiguous. Select a dashboard to inspect its settings.';
          return;
        }
        if (!this.selectDashboard(focus.dashboardId)) return;
      }
      if (focus) {
        const opened =
          focus.dashboardId !== undefined
            ? this.managedDashboardSettings
              ? this.openDashboardSettings(focus.dashboardId, this.dashboardSettingsState.origin)
              : this.openQueueSettings()
            : this.openResponsibilitySettings();
        if (!opened) return;
      }
      this.lastResponsibilityFocus = focus;
      this.ownerViews.consumeFocus(this.ownerViewKey, focus);
    }
    if (this.scope === 'dashboard' && this.active && (restored || reactivated)) this.restoreOwnerFocus();
    this.ownerActive = this.active;
    this.ownerDocument = this.document;
    this.ownerNodeId = this.node?.id ?? '';
    this.ownerDashboardId = this.dashboard?.id ?? '';
  }
  selectDashboard(id: string): boolean {
    if (!this.prepareDashboardChange()) return false;
    if (this.beforeSelectionChange && !this.beforeSelectionChange())
      return false;
    this.ownerRestorePending = false;
    this.selectedDashboard = id;
    this.selection = null;
    this.dashboardMode = 'edit';
    this.queueSettingsOpen = true;
    this.inspectorOpen = true;
    this.responsibilitySettingsOpen = false;
    this.addToolsOpen = false;
    return true;
  }
  selectDashboardFromControl(value: string, control: HTMLSelectElement, origin?: WorkflowDashboardSettingsOrigin): void {
    if (!this.managedDashboardSettings || this.settingsNavigate(origin) && this.settingsTarget(value)) this.selectDashboard(value);
    control.value = this.dashboard?.id ?? '';
  }
  configure(selection: DashboardSelection): void {
    if (!this.prepareDashboardChange()) return;
    if (this.beforeSelectionChange && !this.beforeSelectionChange()) return;
    if (!this.supportedDashboard || this.dashboardMode !== 'edit') return;
    this.ownerRestorePending = false;
    this.selection = selection;
    this.queueSettingsOpen = false;
    this.responsibilitySettingsOpen = false;
    if (!this.selectedItem) this.selection = null;
    if (this.selectedItem) {
      this.inspectorOpen = true;
      this.mobilePanel = 'settings';
      this.focusDashboardSettings();
    }
  }
  createDashboard(source = ''): void {
    if (this.managedDashboardSources) { this.openDashboardSourcePicker(); return; }
    if (!this.prepareDashboardChange()) return;
    if (
      this.dashboardControlsDisabled ||
      this.loading ||
      !this.componentsEditor
    )
      return;
    const view = source
      ? this.supportedViews.find((item) => this.viewIdentity(item) === source)
      : this.defaultView;
    if (!view) return;
    if (this.document && this.node) {
      this.componentsEditor.document = this.document;
      this.componentsEditor.node = this.node;
    }
    this.componentsEditor.selectedView = this.viewIdentity(view);
    this.componentsEditor.addView();
    this.showSources = false;
    this.additionalSource = '';
  }
  applyDetailDocument(document: WorkflowDefinitionDocument) {
    if (this.disabled || this.loading) return;
    this.document = document;
    this.node =
      document.nodes.find((node) => node.id === this.node?.id) ?? this.node;
    this.documentChange.emit(document);
  }
  private change(declaration: WorkflowStepDeclaration) {
    if (this.dashboardControlsDisabled || !this.document || !this.node) return;
    const needsProgressSchema = declaration.dashboards?.some(
      (dashboard) =>
        dashboard.presentation?.workflowStatusPriorityCodes?.length ||
        dashboard.presentation?.filters.some(
          (filter) => filter.field === 'stepProgress',
        ) ||
        dashboard.presentation?.tiles.some(
          (tile) => tile.statusScope === 'stepProgress',
        ) ||
        dashboard.presentation?.columns.some(
          (column) => column.field === 'stepProgress',
        ),
    );
    const needsAssignmentSchema = declaration.dashboards?.some(
      (dashboard) =>
        dashboard.presentation?.columns.some(
          (column) => column.field === 'assigneeDisplayName',
        ) ||
        dashboard.presentation?.filters.some(
          (filter) => filter.field === 'assignment',
        ) ||
        dashboard.presentation?.tiles.some(
          (tile) => tile.statusScope === 'assignment',
        ),
    );
    const updated = replacePresentation(
      needsProgressSchema || needsAssignmentSchema
        ? {
            ...this.document,
            payloadSchemaVersion: requireWorkflowPayloadVersion(
              this.document.payloadSchemaVersion,
              needsAssignmentSchema ? '1.13' : '1.12',
            ),
          }
        : this.document,
      this.node.id,
      declaration,
    );
    // Consecutive controls must build on the latest local edit before parent inputs refresh.
    this.document = updated;
    this.node = updated.nodes.find((node) => node.id === this.node!.id)!;
    this.documentChange.emit(updated);
  }
  private setDashboardAccess(
    change: Pick<
      Partial<NonNullable<WorkflowStepDeclaration['dashboards']>[number]>,
      'queueScope' | 'audienceRoleCodes'
    >,
  ): void {
    if (!this.prepareDashboardChange()) return;
    if (!this.declaration || !this.dashboard) return;
    this.change({
      ...this.declaration,
      dashboards: this.declaration.dashboards!.map((d) =>
        d.id === this.dashboard!.id ? { ...d, ...change } : d,
      ),
    });
  }
  setDashboard(
    change: Partial<NonNullable<WorkflowStepDeclaration['dashboards']>[number]>,
  ) {
    if (!this.prepareDashboardChange()) return;
    if (!this.declaration || !this.dashboard || !this.supportedDashboard)
      return;
    this.change({
      ...this.declaration,
      dashboards: this.declaration.dashboards!.map((d) =>
        d.id === this.dashboard!.id ? { ...d, ...change } : d,
      ),
    });
  }
  removeDashboard(dashboardId?: string): void {
    if (this.managedDashboardRemoval) {
      const document = this.latestDocument?.() ?? this.document, nodeId = this.node?.id, view = this.dashboardRemovalView?.();
      if (!document || !nodeId || !dashboardId?.trim() || !view || !Array.isArray(document.nodes)) return;
      const nodes = document.nodes.filter(node => node?.id === nodeId);
      const dashboards = nodes.length === 1 && nodes[0].typeCode === 'human_step' && Array.isArray(nodes[0].declaration?.dashboards)
        ? nodes[0].declaration.dashboards.filter(dashboard => dashboard?.id === dashboardId) : [];
      if (dashboards.length !== 1 || this.selectedDashboard && this.selectedDashboard !== dashboardId) return;
      const request: WorkflowDashboardRemovalRequest = { nodeId, dashboardId, selection: this.selectedDashboard,
        owner: this.removalOwnerKey(), source: JSON.stringify(document), view, context: this.removalContext() };
      this.removalEmission = request;
      try { if (this.isCurrentDashboardRemovalRequest(request)) this.dashboardRemovalRequested.emit(request); }
      finally { this.removalEmission = null; }
      return;
    }
    if (!this.prepareDashboardChange()) return;
    if (!this.declaration || !this.dashboard) return;
    this.change({
      ...this.declaration,
      dashboards: this.declaration.dashboards?.filter(
        (view) => view.id !== this.dashboard!.id,
      ),
    });
    this.selectDashboard('');
  }
  setStatus(status: string) {
    if (!this.prepareDashboardChange()) return;
    if (this.reviewWorkView) return;
    if (
      !this.dashboard ||
      (status && !this.statuses.some((item) => item.code === status))
    )
      return;
    const presentation = this.presentation
      ? structuredClone(this.presentation)
      : null;
    if (presentation)
      presentation.filters = presentation.filters.map((filter) =>
        filter.field === 'status' && status && filter.initialValue !== status
          ? { ...filter, initialValue: '' }
          : filter,
      );
    this.setDashboard({
      ...(presentation
        ? updateDashboardPresentation(
            this.dashboard,
            presentation,
            this.installedView,
          )
        : {}),
      defaults: { ...this.dashboard!.defaults, status: status || null },
    });
  }
  private editPresentation(
    operation: (presentation: DashboardPresentation) => DashboardPresentation,
  ): void {
    if (!this.prepareDashboardChange()) return;
    if (
      this.disabled ||
      this.loading ||
      !this.supportedDashboard ||
      !this.dashboard ||
      !this.presentation
    )
      return;
    const updated = operation(structuredClone(this.presentation));
    this.setDashboard(
      updateDashboardPresentation(this.dashboard, updated, this.installedView),
    );
  }
  addTile(
    status: string,
    statusScope:
      | 'progress'
      | 'workflow'
      | 'step'
      | 'stepProgress'
      | 'assignment' = 'progress',
  ): void {
    if (!this.prepareDashboardChange()) return;
    if (
      this.reviewWorkView &&
      (statusScope !== 'progress' ||
        (status && !this.reviewWorkStatuses.includes(status)))
    )
      return;
    if (
      this.dashboardControlsDisabled ||
      (statusScope === 'assignment' && !this.assignmentSupported)
    )
      return;
    if ((this.presentation?.tiles.length ?? 0) >= 12) return;
    const choices = dashboardTileStatusChoices(
      this.installedView,
      this.document,
      this.node?.id,
      statusScope,
    );
    if (status && !choices.some((item) => item.code === status)) return;
    let id = 'tile-1',
      suffix = 2;
    while (this.presentation?.tiles.some((item) => item.id === id))
      id = `tile-${suffix++}`;
    this.editPresentation((presentation) => ({
      ...presentation,
      tiles: [
        ...presentation.tiles,
        {
          id,
          label: status
            ? choices.find((item) => item.code === status)!.label
            : 'Total records',
          status: status || null,
          ...(statusScope === 'progress' ? {} : { statusScope }),
          tone: 'neutral',
          filterOnClick: true,
        },
      ],
    }));
    this.configure({ kind: 'tile', id });
    this.addToolsOpen = false;
  }
  addFilter(field: string): void {
    if (!this.prepareDashboardChange()) return;
    if (this.dashboardControlsDisabled) return;
    const filter = this.availableFilters.find((item) => item.field === field);
    if (!filter) return;
    this.editPresentation((presentation) => ({
      ...presentation,
      filters: [
        ...presentation.filters,
        {
          field: filter.field,
          label: filter.label,
          initialValue: field === 'history' ? 'current' : '',
        },
      ],
    }));
    this.configure({ kind: 'filter', id: field });
    this.addToolsOpen = false;
  }
  addColumn(field: string): void {
    if (!this.prepareDashboardChange()) return;
    if (this.dashboardControlsDisabled) return;
    const column = this.availableColumns.find((item) => item.field === field);
    if (!column) return;
    this.editPresentation((presentation) => ({
      ...presentation,
      columns: [
        ...presentation.columns,
        { field: column.field, label: column.label },
      ],
    }));
    this.configure({ kind: 'column', id: field });
    this.addToolsOpen = false;
  }
  setItemLabel(label: string): void {
    if (!this.prepareDashboardChange()) return;
    if (this.selectedTile) this.setTile({ label });
    else if (this.selectedFilter) this.setFilter({ label });
    else if (this.selectedColumn)
      this.editPresentation((presentation) => ({
        ...presentation,
        columns: presentation.columns.map((item) =>
          item.field === this.selection!.id ? { ...item, label } : item,
        ),
      }));
  }
  setTile(change: Partial<DashboardTile>): void {
    if (!this.prepareDashboardChange()) return;
    if (
      this.reviewWorkView &&
      ((change.statusScope && change.statusScope !== 'progress') ||
        (change.status && !this.reviewWorkStatuses.includes(change.status)))
    )
      return;
    if (
      !this.selectedTile ||
      (change.status &&
        !dashboardTileStatusChoices(
          this.installedView,
          this.document,
          this.node?.id,
          change.statusScope ?? this.selectedTile.statusScope ?? 'progress',
        ).some((item) => item.code === change.status)) ||
      (change.tone && !this.tones.includes(change.tone))
    )
      return;
    this.editPresentation((presentation) => ({
      ...presentation,
      tiles: presentation.tiles.map((item) =>
        item.id === this.selection!.id ? { ...item, ...change } : item,
      ),
    }));
  }
  setFilter(change: Partial<DashboardFilter>): void {
    if (!this.prepareDashboardChange()) return;
    if (
      !this.selectedFilter ||
      (change.initialValue != null &&
        this.selectedFilter.field !== 'search' &&
        !this.initialFilterChoices.some(
          (choice) => choice.code === change.initialValue,
        ) &&
        !change.allowedValues?.includes(change.initialValue))
    )
      return;
    this.editPresentation((presentation) => ({
      ...presentation,
      filters: presentation.filters.map((item) =>
        item.field === this.selection!.id ? { ...item, ...change } : item,
      ),
    }));
  }
  selectItem(value: string): void {
    if (!this.prepareDashboardChange()) return;
    const separator = value.indexOf(':');
    const kind = value.slice(0, separator),
      id = value.slice(separator + 1);
    if (['tile', 'filter', 'column'].includes(kind))
      this.configure({ kind: kind as DashboardSelection['kind'], id });
    else if (!this.beforeSelectionChange || this.beforeSelectionChange())
      this.selection = null;
  }
  selectItemFromControl(value: string, control: HTMLSelectElement): void {
    this.selectItem(value);
    control.value = this.selection
      ? `${this.selection.kind}:${this.selection.id}`
      : '';
  }
  toggleFilterChoice(code: string, checked: boolean): void {
    if (!this.prepareDashboardChange()) return;
    const values = this.filterAllowedValues;
    this.setAllowedValues(
      checked ? [...values, code] : values.filter((value) => value !== code),
    );
  }
  setAllowedValues(values: string[]): void {
    if (!this.prepareDashboardChange()) return;
    if (
      !this.selectedFilter ||
      this.selectedFilter.field === 'search' ||
      !values.length ||
      values.some(
        (value) => !this.filterChoices.some((choice) => choice.code === value),
      )
    )
      return;
    this.setFilter({
      allowedValues:
        values.length === this.filterChoices.length ? null : values,
      initialValue: values.includes(this.selectedFilter.initialValue)
        ? this.selectedFilter.initialValue
        : [
              'status',
              'workflowStatus',
              'stepStatus',
              'stepProgress',
              'assignment',
            ].includes(this.selectedFilter.field)
          ? ''
          : values[0],
    });
  }
  setColumnOrder(order: string[]): void {
    if (!this.prepareDashboardChange()) return;
    const presentation = this.presentation;
    if (
      this.scope !== 'dashboard' ||
      this.dashboardMode !== 'edit' ||
      this.disabled ||
      this.loading ||
      !this.supportedDashboard ||
      !presentation ||
      !Array.isArray(order)
    )
      return;
    const fields = presentation.columns.map((column) => column.field),
      currentFields = new Set(fields);
    // The renderer reports the complete finished order, never an add/remove operation.
    if (
      order.length !== fields.length ||
      currentFields.size !== fields.length ||
      new Set(order).size !== fields.length ||
      order.some(
        (field) => typeof field !== 'string' || !currentFields.has(field),
      ) ||
      fields.every((field, index) => field === order[index])
    )
      return;
    this.editPresentation((value) => {
      const columns = new Map(
        value.columns.map((column) => [column.field, column]),
      );
      return { ...value, columns: order.map((field) => columns.get(field)!) };
    });
  }
  moveSelected(offset: number): void {
    if (!this.prepareDashboardChange()) return;
    if (this.dashboardMode !== 'edit') return;
    const index = this.selectedIndex,
      target = index + offset;
    if (
      ![-1, 1].includes(offset) ||
      index < 0 ||
      target < 0 ||
      target >= this.selectedCount ||
      !this.selection
    )
      return;
    const key =
      this.selection.kind === 'tile'
        ? 'tiles'
        : this.selection.kind === 'filter'
          ? 'filters'
          : 'columns';
    this.editPresentation((presentation) => {
      const items = presentation[key];
      [items[index], items[target]] = [items[target], items[index]];
      return presentation;
    });
  }
  removeSelected(): void {
    if (!this.prepareDashboardChange()) return;
    if (
      !this.selection ||
      this.requiredColumn ||
      this.dashboardControlsDisabled
    )
      return;
    this.editPresentation((presentation) =>
      this.selection!.kind === 'tile'
        ? {
            ...presentation,
            tiles: presentation.tiles.filter(
              (item) => item.id !== this.selection!.id,
            ),
          }
        : this.selection!.kind === 'filter'
          ? {
              ...presentation,
              filters: presentation.filters.filter(
                (item) => item.field !== this.selection!.id,
              ),
            }
          : {
              ...presentation,
              columns: presentation.columns.filter(
                (item) => item.field !== this.selection!.id,
              ),
            },
    );
    this.selection = null;
  }
  setContext(
    contextPlacement: WorkflowStepDeclaration['detail']['contextPlacement'],
  ) {
    if (!this.prepareDashboardChange()) return;
    if (this.declaration)
      this.change({
        ...this.declaration,
        detail: { ...this.declaration.detail, contextPlacement },
      });
  }
}
